import { Scene, Vector3, Ray, TransformNode, AbstractMesh, Bone, Skeleton, Color3, AnimationGroup, Matrix, Quaternion, SceneLoader } from '@babylonjs/core';
import { EventBus } from '../core/EventBus';
import { logger } from '../core/Logger';
import { DebugRenderer } from '../core/DebugRenderer';
import { ServiceRegistry } from '../core/ServiceRegistry';
import type { StatePhase } from '../config/characters';
import { StateManager } from '../state/StateManager';
import { isEmbeddedReference, type ExtractionResult } from '../assets/EntityAssetExtractor';
import type { CharacterAsset } from '../assets/CharacterLoader';
import { createVideoTextureInstance, applyVideoToMaterial } from '../assets/VideoTextureUtils';
import { getBoneWorldPosition, getBoneWorldForward } from './BoneAttachmentSystem';
import { createProjectileMesh } from './MeshFactory';
import { ProjectileSystem } from './ProjectileSystem';
import { CombatEffectsController } from './CombatEffectsController';
import {
  AttackDef,
  WeaponInstance,
  ProjectileInstance,
  ActiveAttack,
  ProjectileHandoverContext,
  ProjectileLaunchDirective,
  CombatAttackStartEvent,
  CombatDamageEvent,
  CombatDeathEvent,
  HitResult,
  InterruptContext,
  InterruptReason,
  AttackEndContext,
  AttackTickContext,
  ChannelTickContext,
  ChannelEndContext,
  ChannelStartContext,
  ChannelActions,
  ChannelHitDetectionConfig,
  ChannelHitResult,
  ChannelPresetContext,
  ChannelPreset,
  BoneLightOptions,
} from '../types/combat';
import { Character } from './Character';
import type { AttackContext as CharacterAttackContext, DamageContext as CharacterDamageContext, HitContext as CharacterHitContext } from '../config/characters';
import type { ParticleManager } from './ParticleManager';

type ProjectileLaunchParams = {
  position: Vector3;
  velocity: Vector3;
  direction: Vector3;
  mesh?: AbstractMesh | null;
};

type AttackRequestOptions = {
  binding?: 'action' | 'action2' | 'action3';
  isHeld?: () => boolean;
};

/**
 * CombatSystem manages all combat state: weapon/projectile lifecycle, hit detection, damage application.
 * Single owner of damage logic - only entity that calls character.takeDamage().
 */
export class CombatSystem {
  private scene: Scene;
  private eventBus: EventBus;
  private debugRenderer: DebugRenderer;
  private particleManager: ParticleManager | null = null;
  private characterRegistry: Map<string, Character> = new Map();
  private projectileSystem: ProjectileSystem;
  private effectsController: CombatEffectsController;
  
  private activeWeapons: Map<string, WeaponInstance> = new Map();
  private activeAttacks: Map<string, ActiveAttack> = new Map(); // Key: attackerId
  private nextInstanceId = 0;
  private nextEmissionId = 0;
  
  /** Accumulated game time (affected by time scale) - use getTimeSeconds() to read */
  private _gameTimeElapsed = 0;
  
  private debugEnabled = false;
  private debugVisualizations: Map<string, AbstractMesh[]> = new Map(); // Key: weapon/projectile instance ID

  private lastHit: { nodeId: string; nodeName: string } | null = null;

  constructor(scene: Scene, eventBus: EventBus) {
    this.scene = scene;
    this.eventBus = eventBus;
    this.debugRenderer = DebugRenderer.getInstance(scene);
    
    // Initialize ProjectileSystem with dependency injection
    this.projectileSystem = new ProjectileSystem(
      scene,
      () => this.getTimeSeconds(),
      (ray, excludeOwnerId, characters, hitRadius) => this.raycastCharacters(ray, excludeOwnerId, characters, hitRadius)
    );
    
    // Initialize CombatEffectsController
    this.effectsController = new CombatEffectsController(scene);
  }

  /**
   * Set the particle manager for channel actions
   */
  setParticleManager(pm: ParticleManager): void {
    this.particleManager = pm;
    this.projectileSystem.setParticleManager(pm);
  }

  /**
   * Register a character for hit detection
   */
  registerCharacter(character: Character): void {
    this.characterRegistry.set(character.getId(), character);
  }

  /**
   * Unregister a character
   */
  unregisterCharacter(characterId: string): void {
    this.characterRegistry.delete(characterId);
  }

  /**
   * Get embedded entities from a character's asset cache
   */
  private getEmbeddedEntitiesForCharacter(character: Character): ExtractionResult | undefined {
    const characterAsset = (character as any).characterAsset as CharacterAsset | undefined;
    return characterAsset?.embeddedEntities;
  }

  /**
   * Create ChannelActions API for a specific attacker
   */
  private createChannelActions(attacker: Character, activeAttack: ActiveAttack): ChannelActions {
    const self = this;
    
    return {
      // === Particles ===
      startParticles: (options) => {
        const emissionId = `emission_${self.nextEmissionId++}`;
        
        // Track emission for cleanup
        if (!activeAttack.activeParticleEmissions) {
          activeAttack.activeParticleEmissions = [];
        }
        activeAttack.activeParticleEmissions.push(emissionId);
        
        // Use ParticleManager if available
        if (self.particleManager && options.preset) {
          const position = options.position ?? attacker.character.getAbsolutePosition();
          self.particleManager.spawnOneShot(options.preset, position, {
            scale: options.scale,
            countMultiplier: options.countMultiplier,
            direction: options.direction,
          });
        }
        
        logger.core.debug(`[CombatSystem] Started particles: ${emissionId} (${options.preset})`);
        return emissionId;
      },
      
      stopParticles: (emissionId) => {
        if (activeAttack.activeParticleEmissions) {
          const idx = activeAttack.activeParticleEmissions.indexOf(emissionId);
          if (idx >= 0) {
            activeAttack.activeParticleEmissions.splice(idx, 1);
          }
        }
        logger.core.debug(`[CombatSystem] Stopped particles: ${emissionId}`);
      },
      
      burstParticles: (preset, options) => {
        if (self.particleManager) {
          const position = options?.position ?? attacker.character.getAbsolutePosition();
          self.particleManager.spawnOneShot(preset, position, {
            scale: options?.scale,
            countMultiplier: options?.countMultiplier,
            direction: options?.direction,
          });
        }
      },
      
      updateParticleDirection: (_emissionId, _direction) => {
        // TODO: Implement continuous emission direction updates
        // For now, particles spawn with initial direction
      },
      
      spawnParticlesAtBone: (options) => {
        if (!self.particleManager) return;
        
        // Get bone position
        let position = getBoneWorldPosition(attacker, options.bone);
        if (!position) {
          position = attacker.character.getAbsolutePosition();
        }
        
        // Apply offset in bone space
        if (options.offset) {
          position = position.add(options.offset);
        }
        
        // Apply position jitter
        if (options.jitter?.position) {
          const j = options.jitter.position;
          position = position.add(new Vector3(
            (Math.random() - 0.5) * 2 * j,
            (Math.random() - 0.5) * 2 * j,
            (Math.random() - 0.5) * 2 * j
          ));
        }
        
        // Determine direction
        let direction: Vector3;
        if (options.direction instanceof Vector3) {
          direction = options.direction.clone();
        } else if (options.direction === 'bone') {
          // Use getForwardDirection() as fallback - this is the intended facing direction
          // even during turn animations when mesh rotation is locked
          direction = getBoneWorldForward(attacker, options.bone) ?? attacker.getForwardDirection();
        } else {
          // Default to 'facing' - use getForwardDirection() for the same reason
          direction = attacker.getForwardDirection();
        }
        
        // Apply direction jitter (in degrees)
        if (options.jitter?.direction) {
          const jDeg = options.jitter.direction;
          const jRad = (jDeg * Math.PI) / 180;
          const rotX = (Math.random() - 0.5) * 2 * jRad;
          const rotY = (Math.random() - 0.5) * 2 * jRad;
          // Simple rotation approximation
          direction = new Vector3(
            direction.x + Math.sin(rotY) * direction.z,
            direction.y + Math.sin(rotX),
            direction.z + Math.cos(rotY) * direction.x
          ).normalize();
        }
        
        // Calculate scale with jitter
        let scale = options.scale ?? 1.0;
        if (options.jitter?.scale) {
          const sj = options.jitter.scale;
          scale *= 1 + (Math.random() - 0.5) * 2 * sj;
        }
        
        // Spawn particles
        const count = options.count ?? 1;
        for (let i = 0; i < count; i++) {
          self.particleManager.spawnOneShot(options.preset, position, {
            scale,
            direction,
            countMultiplier: 1,
          });
        }
      },
      
      // === Hit Detection ===
      detectTargets: (config) => {
        return self.performChannelHitDetection(attacker, activeAttack, config);
      },
      
      applyDamage: (targetId, amount) => {
        const target = self.characterRegistry.get(targetId);
        if (target) {
          self.applyChannelDamage(attacker, target, amount, activeAttack);
        }
      },
      
      applyDamageToAll: (amount) => {
        // Use last detected targets from state
        const state = activeAttack.channelState ?? {};
        const lastHits = state.lastHits as ChannelHitResult[] | undefined;
        if (lastHits) {
          for (const hit of lastHits) {
            if (hit.canDamage) {
              const target = self.characterRegistry.get(hit.targetId);
              if (target) {
                self.applyChannelDamage(attacker, target, amount, activeAttack);
              }
            }
          }
        }
      },
      
      // === Character State ===
      getFacing: () => {
        // Use getForwardDirection() which returns the intended facing direction
        // This is correct even during turn animations when mesh rotation is locked
        return attacker.getForwardDirection().normalize();
      },
      
      getPosition: () => {
        return attacker.character.getAbsolutePosition().clone();
      },
      
      getBonePosition: (boneName) => {
        const characterAsset = (attacker as any).characterAsset;
        const skeleton: Skeleton | undefined = characterAsset?.skeleton;
        const rootMesh: AbstractMesh | undefined = characterAsset?.rootMesh;
        if (!skeleton || !rootMesh) return null;
        
        const bone = skeleton.bones.find((b: Bone) => b.name === boneName);
        if (!bone) return null;
        
        // Get bone position in world space by using the linked transform node
        // bone.getAbsolutePosition() returns skeleton-local, we need world space
        const transform = bone.getTransformNode();
        if (transform) {
          return transform.getAbsolutePosition().clone();
        }
        
        // Fallback: manually transform bone position by mesh world matrix
        const boneLocalPos = bone.getAbsolutePosition();
        const worldPos = Vector3.TransformCoordinates(boneLocalPos, rootMesh.getWorldMatrix());
        return worldPos;
      },
      
      getBoneForward: (boneName) => {
        const characterAsset = (attacker as any).characterAsset;
        const skeleton: Skeleton | undefined = characterAsset?.skeleton;
        if (!skeleton) return null;
        
        const bone = skeleton.bones.find((b: Bone) => b.name === boneName);
        if (!bone) return null;
        
        const transform = bone.getTransformNode();
        if (!transform) return null;
        
        return transform.getDirection(Vector3.Forward()).normalize();
      },
      
      // === Effects ===
      triggerImpact: (preset, intensity) => {
        self.eventBus.emit('trigger-impact', { preset, intensity });
      },
      
      playSound: (soundId) => {
        self.eventBus.emit('play-sound', { soundId });
      },
      
      // === Lights ===
      createBoneLight: (options: BoneLightOptions) => {
        if (!activeAttack.activeChannelLights) {
          activeAttack.activeChannelLights = [];
        }
        return self.effectsController.createBoneLight(attacker, options, activeAttack.activeChannelLights);
      },
      
      updateBoneLightPosition: (lightId: string, boneName: string, offset?: Vector3) => {
        self.effectsController.updateBoneLightPosition(attacker, lightId, boneName, offset);
      },
      
      updateBoneLightColor: (lightId: string, color: Color3, intensity?: number) => {
        self.effectsController.updateBoneLightColor(lightId, color, intensity);
      },
      
      destroyLight: (lightId: string) => {
        self.effectsController.destroyLight(lightId, activeAttack.activeChannelLights);
      },
    };
  }

  /**
   * Perform hit detection for channeled attacks
   */
  private performChannelHitDetection(
    attacker: Character,
    activeAttack: ActiveAttack,
    config: ChannelHitDetectionConfig
  ): ChannelHitResult[] {
    const results: ChannelHitResult[] = [];
    const now = this.getTimeSeconds();
    const attackerPos = attacker.character.getAbsolutePosition();
    // Use getForwardDirection() which is correct even during turn animations
    const attackerForward = attacker.getForwardDirection().normalize();
    
    // Initialize damage tracking if needed
    if (!activeAttack.targetDamageTimes) {
      activeAttack.targetDamageTimes = new Map();
    }
    
    for (const [targetId, target] of this.characterRegistry) {
      // Don't hit self
      if (targetId === attacker.getId()) continue;
      
      const targetPos = target.character.getAbsolutePosition();
      const toTarget = targetPos.subtract(attackerPos);
      const distance = toTarget.length();
      
      // Range check
      if (distance > config.range) continue;
      
      // Shape-specific checks
      let inShape = false;
      
      switch (config.type) {
        case 'sphere':
          inShape = distance <= config.width;
          break;
          
        case 'cylinder':
          // Check if target is within cylinder radius of the forward line
          const projLength = Vector3.Dot(toTarget, attackerForward);
          if (projLength >= 0 && projLength <= config.range) {
            const projPoint = attackerPos.add(attackerForward.scale(projLength));
            const perpDist = targetPos.subtract(projPoint).length();
            inShape = perpDist <= config.width;
          }
          break;
          
        case 'cone':
        default:
          // Check angle from forward direction
          const toTargetNorm = toTarget.normalize();
          const dot = Vector3.Dot(toTargetNorm, attackerForward);
          const angleRad = Math.acos(Math.min(1, Math.max(-1, dot)));
          const halfAngleRad = (config.width * Math.PI) / 180;
          inShape = angleRad <= halfAngleRad;
          break;
      }
      
      if (!inShape) continue;
      
      // Check damage interval
      const lastDamageTime = activeAttack.targetDamageTimes.get(targetId);
      const timeSinceDamage = lastDamageTime !== undefined ? now - lastDamageTime : undefined;
      const damageInterval = config.damageInterval ?? 0.25;
      const canDamage = timeSinceDamage === undefined || timeSinceDamage >= damageInterval;
      
      results.push({
        targetId,
        target,
        distance,
        position: targetPos.clone(),
        timeSinceDamage,
        canDamage,
      });
    }
    
    // Sort by distance
    results.sort((a, b) => a.distance - b.distance);
    
    return results;
  }

  /**
   * Apply damage from a channeled attack
   */
  private applyChannelDamage(
    attacker: Character,
    target: Character,
    amount: number,
    activeAttack: ActiveAttack
  ): void {
    const now = this.getTimeSeconds();
    
    // Update damage time tracking
    if (!activeAttack.targetDamageTimes) {
      activeAttack.targetDamageTimes = new Map();
    }
    activeAttack.targetDamageTimes.set(target.getId(), now);
    
    // Apply damage
    const killed = target.takeDamage(amount);
    
    // Get impactFX from attackDef
    const impactFX = activeAttack.attackDef.impactFX;
    const impactPosition = target.character.getAbsolutePosition().clone();
    
    // Emit event
    const damageEvent: CombatDamageEvent = {
      attackerId: attacker.getId(),
      targetId: target.getId(),
      damage: amount,
      position: impactPosition,
      source: 'melee', // Could add 'beam' source type
      attackDefId: activeAttack.attackDef.id,
      impactFX,
    };
    this.eventBus.emit('combat:damage', damageEvent);
    
    // Spawn impact FX
    if (impactFX) {
      const fxManager = ServiceRegistry.getFXManager();
      if (fxManager) {
        fxManager.spawnFX(impactFX, impactPosition);
      }
    }
    
    if (killed) {
      this.eventBus.emit('combat:death', {
        characterId: target.getId(),
        killerId: attacker.getId(),
      } as CombatDeathEvent);
    }
    
    logger.core.debug(`[CombatSystem] Channel damage: ${attacker.getId()} -> ${target.getId()} (${amount})`);
  }

  private updateChanneledAttack(activeAttack: ActiveAttack, attacker: Character, _deltaTime: number): void {
    const config = activeAttack.attackDef.channeled;
    if (!config) return;

    const now = this.getTimeSeconds();
    const channelActions = this.createChannelActions(attacker, activeAttack);

    // Initialize on first tick
    if (activeAttack.channelStartTime === undefined) {
      activeAttack.channelStartTime = now;
      activeAttack.lastTickTime = now;
      activeAttack.tickCount = 0;
      activeAttack.channelState = {}; // Initialize shared state for presets
      
      void this.ensureAttachedMeshes(attacker, activeAttack);
      
      // Spawn static FX attached to bones via effectsController
      if (config.staticFx && config.staticFx.length > 0) {
        const embeddedEntities = this.getEmbeddedEntitiesForCharacter(attacker);
        activeAttack.activeStaticFx = this.effectsController.spawnStaticFx(attacker, config.staticFx, embeddedEntities);
      }
      
      // Execute preset onStart hooks
      const presets = config.presets ?? [];
      for (const preset of presets) {
        if (preset.onStart) {
          try {
            const presetConfig = config.presetConfigs?.[preset.id] ?? {};
            const ctx = this.buildPresetContext(attacker, activeAttack, channelActions, presetConfig, 0, 0);
            preset.onStart(ctx);
          } catch (error) {
            logger.core.error(`CombatSystem: Preset '${preset.id}' onStart threw for ${attacker.getId()}`, error);
          }
        }
      }
      
      // Execute legacy onChannelStart hook
      if (activeAttack.attackDef.onChannelStart) {
        try {
          const startContext: ChannelStartContext = {
            attackerId: attacker.getId(),
            attack: activeAttack.attackDef,
            actions: channelActions,
            state: activeAttack.channelState,
          };
          activeAttack.attackDef.onChannelStart(startContext);
        } catch (error) {
          logger.core.error(`CombatSystem: onChannelStart hook threw for ${attacker.getId()}`, error);
        }
      }
    }

    const holdCheck = activeAttack.isHeld;
    const buttonHeld = holdCheck ? holdCheck() : true;

    const channelDuration = now - (activeAttack.channelStartTime ?? now);
    const maxDuration = config.maxDuration ?? Number.POSITIVE_INFINITY;
    const minDuration = config.minDuration ?? 0;

    // Check for ending conditions:
    // 1. Max duration exceeded → end with 'completed'
    // 2. Button released AND min duration met → end with 'player'
    const canEarlyExit = channelDuration >= minDuration;
    if (channelDuration >= maxDuration) {
      this.endChanneledAttack(attacker, activeAttack, 'completed');
      return;
    }
    if (!buttonHeld && canEarlyExit) {
      this.endChanneledAttack(attacker, activeAttack, 'player');
      return;
    }

    const tickInterval = config.tickInterval ?? 0.1;
    const shouldTick = activeAttack.lastTickTime === undefined || (now - activeAttack.lastTickTime) >= tickInterval;

    if (shouldTick) {
      const tickCount = (activeAttack.tickCount ?? 0) + 1;
      activeAttack.lastTickTime = now;
      activeAttack.tickCount = tickCount;
      
      const normalizedTime = maxDuration !== Number.POSITIVE_INFINITY 
        ? Math.min(1, channelDuration / maxDuration)
        : 0;

      // Execute preset onTick hooks
      const presets = config.presets ?? [];
      logger.combat.debug(`Executing ${presets.length} preset onTick hooks for tick ${tickCount}`);
      for (const preset of presets) {
        if (preset.onTick) {
          try {
            const presetConfig = config.presetConfigs?.[preset.id] ?? {};
            const ctx = this.buildPresetContext(attacker, activeAttack, channelActions, presetConfig, channelDuration, tickCount);
            logger.combat.debug(`Calling preset '${preset.id}' onTick, normalizedTime=${ctx.normalizedTime.toFixed(3)}`);
            preset.onTick(ctx);
            logger.combat.debug(`After preset '${preset.id}' onTick, channelState keys=${Object.keys(activeAttack.channelState ?? {}).join(', ')}`);
          } catch (error) {
            logger.core.error(`CombatSystem: Preset '${preset.id}' onTick threw for ${attacker.getId()}`, error);
          }
        }
      }

      // Build tick context with actions
      // Ensure channelState exists
      if (!activeAttack.channelState) {
        activeAttack.channelState = {};
      }
      const tickContext: ChannelTickContext = {
        attackerId: attacker.getId(),
        attack: activeAttack.attackDef,
        channelDuration,
        tickCount,
        buttonHeld,
        normalizedTime,
        actions: channelActions,
        state: activeAttack.channelState,
      };

      // Execute legacy onChannelTick hook
      const hook = activeAttack.attackDef.onChannelTick;
      if (hook) {
        try {
          hook(tickContext);
        } catch (error) {
          logger.core.error(`CombatSystem: onChannelTick hook threw for ${attacker.getId()}`, error);
        }
      }

      if (tickContext.facingOverride) {
        const dir = tickContext.facingOverride;
        if (dir.lengthSquared() > 1e-4) {
          const forward = dir.normalize();
          const yaw = Math.atan2(forward.x, forward.z);
          attacker.character.rotation.y = yaw;
        }
      }

      if (tickContext.attachedMeshOverrides && activeAttack.attachedMeshInstances) {
        for (const override of tickContext.attachedMeshOverrides) {
          const match = activeAttack.attachedMeshInstances.find((m) => m.mesh === override.mesh.mesh);
          if (!match) continue;
          if (override.position) {
            override.mesh.mesh.position.copyFrom(override.position);
          }
          if (override.rotation) {
            override.mesh.mesh.rotation.copyFrom(override.rotation);
          }
          if (override.scale) {
            override.mesh.mesh.scaling.copyFrom(override.scale);
          }
        }
      }

      // Check for preset-controlled projectile spawn request
      const spawnRequest = activeAttack.channelState?.spawnProjectileRequest as {
        position: Vector3;
        direction: Vector3;
        spawnIndex: number;
      } | undefined;

      logger.combat.debug(`Tick ${tickCount}, spawnRequest=${!!spawnRequest}, hasProjectileSpec=${!!activeAttack.attackDef.projectile}, channelState keys=${Object.keys(activeAttack.channelState ?? {}).join(', ')}`);

      if (spawnRequest && activeAttack.attackDef.projectile) {
        // Preset requested a projectile spawn at specific position/direction
        const spec = activeAttack.attackDef.projectile;
        
        // For 'facing' mode, flatten direction to horizontal (XZ plane) so projectiles
        // don't shoot up/down when running on sloped spline paths
        let direction = spawnRequest.direction;
        if (spec.directionMode === 'facing' || spec.directionMode === undefined) {
          direction = new Vector3(direction.x, 0, direction.z);
          if (direction.lengthSquared() < 1e-4) {
            direction = new Vector3(0, 0, 1);
          }
        }
        direction = direction.normalize();
        const velocity = direction.scale(spec.speed ?? 15);
        
        const launch: ProjectileLaunchParams = {
          position: spawnRequest.position,
          velocity,
          direction,
        };
        
        this.spawnProjectile(attacker, activeAttack.attackDef, launch);
        
        // Clear the request
        delete activeAttack.channelState!.spawnProjectileRequest;
      } else if (activeAttack.attackDef.projectile && !config.presets?.some(p => p.id.startsWith('beam_projectiles'))) {
        // Legacy behavior: spawn projectile every tick if no beam preset is controlling it
        const launch = this.resolveProjectileLaunch(attacker, activeAttack);
        if (launch) {
          this.spawnProjectile(attacker, activeAttack.attackDef, launch);
        }
      }
      
      // Handle static FX spawn requests from presets
      const staticFxSpawnRequest = activeAttack.channelState?.spawnStaticFxRequest as {
        id?: string;
        assetPath: string;
        boneName: string;
        scale: number;
        offset?: Vector3;
        orientationMode?: 'bone' | 'world' | 'facing';
        forward?: Vector3;
        up?: Vector3;
        localRotation?: { x?: number; y?: number; z?: number };
        playbackRate?: number;
        videoLoopRange?: { start: number; end: number };
      }[] | undefined;
      
      if (staticFxSpawnRequest && staticFxSpawnRequest.length > 0) {
        if (!activeAttack.activeStaticFx) {
          activeAttack.activeStaticFx = [];
        }
        
        const embeddedEntities = this.getEmbeddedEntitiesForCharacter(attacker);
        const characterAsset = (attacker as any).characterAsset as CharacterAsset | undefined;
        const skeleton = characterAsset?.skeleton;
        const rootMesh = characterAsset?.rootMesh;
        
        for (const fxRequest of staticFxSpawnRequest) {
          try {
            // Create a minimal ProjectileSpec for the static FX
            const fxSpec = {
              assetId: fxRequest.id ?? `staticFx_${Date.now()}`,
              assetPath: fxRequest.assetPath,
              type: 'static' as const,
              speed: 0,
              directionMode: 'bone' as const,
              scale: fxRequest.scale,
            };
            
            const fxMesh = createProjectileMesh(this.scene, fxSpec, `staticFx_${fxRequest.id ?? Date.now()}`, embeddedEntities);
            if (fxMesh) {
              const bone = skeleton?.bones.find((b: Bone) => b.name === fxRequest.boneName);
              const orientationMode = fxRequest.orientationMode ?? 'bone';
              
              if (bone && rootMesh) {
                // Attach to bone for position tracking
                fxMesh.attachToBone(bone, rootMesh);
                
                if (fxRequest.offset) {
                  fxMesh.position.copyFrom(fxRequest.offset);
                } else {
                  fxMesh.position.set(0, 0, 0);
                }
                
                // Apply orientation based on mode
                if (orientationMode === 'world') {
                  // Use world-aligned orientation (ignores bone rotation)
                  // We need to counter-rotate the bone's rotation
                  fxMesh.billboardMode = AbstractMesh.BILLBOARDMODE_NONE;
                  
                  // Calculate rotation to align with specified forward/up or defaults
                  const forward = fxRequest.forward ?? new Vector3(0, 0, 1);
                  const up = fxRequest.up ?? new Vector3(0, 1, 0);
                  
                  // Create rotation matrix from forward/up
                  const rotationMatrix = Matrix.LookAtLH(Vector3.Zero(), forward, up);
                  rotationMatrix.invert();
                  
                  // Store the world orientation for update loop
                  (fxMesh as any)._worldOrientation = { forward, up };
                  
                } else if (orientationMode === 'facing') {
                  // Align to character facing direction (horizontal beam)
                  const forward = fxRequest.forward ?? new Vector3(0, 0, 1);
                  const up = fxRequest.up ?? new Vector3(0, 1, 0);
                  
                  // Store for update loop - will use character's rotation
                  (fxMesh as any)._facingOrientation = { forward, up };
                }
                // For 'bone' mode, no extra rotation needed - inherits bone rotation
              } else if (rootMesh) {
                fxMesh.parent = rootMesh;
              }
              
              // Apply additional local rotation if specified
              if (fxRequest.localRotation) {
                const degToRad = Math.PI / 180;
                fxMesh.rotation.x += (fxRequest.localRotation.x ?? 0) * degToRad;
                fxMesh.rotation.y += (fxRequest.localRotation.y ?? 0) * degToRad;
                fxMesh.rotation.z += (fxRequest.localRotation.z ?? 0) * degToRad;
              }
              
              fxMesh.setEnabled(true);
              fxMesh.isVisible = true;
              
              // Apply per-instance video settings override if specified
              // This requires recreating the video texture with custom rate/loopRange
              const needsCustomVideo = (fxRequest.playbackRate !== undefined && fxRequest.playbackRate !== 1.0) || 
                                       fxRequest.videoLoopRange !== undefined;
              if (needsCustomVideo) {
                const applyVideoOverride = (mesh: AbstractMesh) => {
                  // Check if this mesh had a video texture applied (from createProjectileMesh)
                  const existingVideoTexture = mesh.metadata?.hmVideoTexture;
                  if (existingVideoTexture?.video) {
                    // Get video src and recreate with custom settings
                    const videoSrc = existingVideoTexture.video.src;
                    if (videoSrc) {
                      const scene = mesh.getScene();
                      
                      // Create a new video texture instance with custom settings
                      const newTexture = createVideoTextureInstance(
                        scene,
                        videoSrc,
                        true, // autoplay
                        true, // loop (may be overridden by loopRange)
                        fxRequest.playbackRate ?? 1.0,
                        fxRequest.videoLoopRange
                      );
                      
                      // Apply to material
                      if (mesh.material) {
                        applyVideoToMaterial(mesh.material, newTexture, 1.0, 'additive');
                      }
                      
                      // Update metadata reference
                      mesh.metadata.hmVideoTexture = newTexture;
                      mesh.metadata.hmVideoTextureOwned = true;
                      
                      logger.combat.debug(`  Created new video texture with playbackRate=${fxRequest.playbackRate}, loopRange=${JSON.stringify(fxRequest.videoLoopRange)} on ${mesh.name}`);
                    }
                  }
                  mesh.getChildMeshes?.().forEach(applyVideoOverride);
                };
                applyVideoOverride(fxMesh);
              }
              
              // Store with ID for later cleanup
              (fxMesh as any)._staticFxId = fxRequest.id;
              (fxMesh as any)._orientationMode = orientationMode;
              activeAttack.activeStaticFx.push(fxMesh);
              logger.combat.debug(`Spawned static FX '${fxRequest.assetPath}' (id=${fxRequest.id}) attached to bone '${fxRequest.boneName}' mode='${orientationMode}'`);
            }
          } catch (error) {
            logger.combat.error(`Failed to spawn static FX '${fxRequest.assetPath}':`, error);
          }
        }
        
        delete activeAttack.channelState!.spawnStaticFxRequest;
      }
      
      // Update static FX orientations for world/facing modes
      if (activeAttack.activeStaticFx && activeAttack.activeStaticFx.length > 0) {
        const characterAsset = (attacker as any).characterAsset as CharacterAsset | undefined;
        
        for (const fxMesh of activeAttack.activeStaticFx) {
          const orientationMode = (fxMesh as any)._orientationMode as string | undefined;
          
          if (orientationMode === 'world' && (fxMesh as any)._worldOrientation) {
            // World mode: counter-rotate to maintain world alignment, then apply desired orientation
            const parentBone = fxMesh.parent;
            if (parentBone && parentBone.getWorldMatrix) {
              const worldMatrix = parentBone.getWorldMatrix();
              const boneRotationQuat = new Quaternion();
              worldMatrix.decompose(undefined, boneRotationQuat, undefined);
              
              // Get the stored desired orientation
              const { forward, up } = (fxMesh as any)._worldOrientation as { forward: Vector3; up: Vector3 };
              
              // Create quaternion for desired world orientation using LookAt
              const desiredQuat = Quaternion.FromLookDirectionLH(forward, up);
              
              // Invert bone rotation to cancel it out, then apply desired orientation
              boneRotationQuat.invertInPlace();
              const finalQuat = boneRotationQuat.multiply(desiredQuat);
              fxMesh.rotationQuaternion = finalQuat;
            }
          } else if (orientationMode === 'facing' && (fxMesh as any)._facingOrientation && characterAsset?.rootMesh) {
            // Facing mode: align with character facing direction
            const parentBone = fxMesh.parent;
            if (parentBone && parentBone.getWorldMatrix) {
              const worldMatrix = parentBone.getWorldMatrix();
              const boneRotationQuat = new Quaternion();
              worldMatrix.decompose(undefined, boneRotationQuat, undefined);
              
              // Get the stored desired orientation
              const { forward, up } = (fxMesh as any)._facingOrientation as { forward: Vector3; up: Vector3 };
              
              // Get character's Y rotation
              const characterRotation = characterAsset.rootMesh.rotation.y;
              
              // Rotate the forward direction by character's facing
              const cosY = Math.cos(characterRotation);
              const sinY = Math.sin(characterRotation);
              const rotatedForward = new Vector3(
                forward.x * cosY + forward.z * sinY,
                forward.y,
                -forward.x * sinY + forward.z * cosY
              );
              
              // Create quaternion for desired orientation
              const desiredQuat = Quaternion.FromLookDirectionLH(rotatedForward, up);
              
              // Invert bone rotation and apply desired facing orientation
              boneRotationQuat.invertInPlace();
              const finalQuat = boneRotationQuat.multiply(desiredQuat);
              fxMesh.rotationQuaternion = finalQuat;
            }
          }
        }
      }
      
      // Handle static FX despawn requests from presets
      const staticFxDespawnRequest = activeAttack.channelState?.despawnStaticFxRequest as string[] | undefined;
      
      if (staticFxDespawnRequest && staticFxDespawnRequest.length > 0 && activeAttack.activeStaticFx) {
        for (const fxId of staticFxDespawnRequest) {
          const fxIndex = activeAttack.activeStaticFx.findIndex(m => (m as any)._staticFxId === fxId);
          if (fxIndex >= 0) {
            const fxMesh = activeAttack.activeStaticFx[fxIndex];
            if (fxMesh && !fxMesh.isDisposed()) {
              fxMesh.dispose();
              logger.combat.debug(`Despawned static FX id=${fxId}`);
            }
            activeAttack.activeStaticFx.splice(fxIndex, 1);
          }
        }
        
        delete activeAttack.channelState!.despawnStaticFxRequest;
      }
    }

  }

  private endChanneledAttack(
    attacker: Character,
    activeAttack: ActiveAttack,
    reason: 'completed' | InterruptReason
  ): void {
    const config = activeAttack.attackDef.channeled;
    const animController = attacker.animationController;
    const channelActions = this.createChannelActions(attacker, activeAttack);

    if (animController) {
      // Check if we're using loop range animation (has intro/loop/outro)
      if (animController.isInLoopRange()) {
        // Release to play the outro, then cleanup
        animController.releaseLoopRange(() => {
          // Outro complete - end attack after outro plays
          this.finalizeChanneledAttackEnd(attacker, activeAttack, reason, channelActions);
        });
        return; // Wait for outro to complete
      } else {
        // Standard looping - stop immediately
        animController.stopLooping();
        if (config?.endAnimation) {
          animController.playOneShot(config.endAnimation);
        }
      }
    }

    this.finalizeChanneledAttackEnd(attacker, activeAttack, reason, channelActions);
  }

  private finalizeChanneledAttackEnd(
    attacker: Character,
    activeAttack: ActiveAttack,
    reason: 'completed' | InterruptReason,
    channelActions: ChannelActions
  ): void {
    const config = activeAttack.attackDef.channeled;
    const duration = activeAttack.channelStartTime !== undefined ? (this.getTimeSeconds() - activeAttack.channelStartTime) : 0;
    
    // Execute preset onEnd hooks
    const presets = config?.presets ?? [];
    for (const preset of presets) {
      if (preset.onEnd) {
        try {
          const presetConfig = config?.presetConfigs?.[preset.id] ?? {};
          const ctx = this.buildPresetContext(attacker, activeAttack, channelActions, presetConfig, duration, activeAttack.tickCount ?? 0);
          const endReason = reason === 'completed' ? 'completed' : (reason === 'player' ? 'cancelled' : 'interrupted');
          preset.onEnd({ ...ctx, reason: endReason });
        } catch (error) {
          logger.core.error(`CombatSystem: Preset '${preset.id}' onEnd threw for ${attacker.getId()}`, error);
        }
      }
    }
    
    // Cleanup any remaining particle emissions
    if (activeAttack.activeParticleEmissions) {
      for (const emissionId of activeAttack.activeParticleEmissions) {
        logger.core.debug(`[CombatSystem] Cleaning up particle emission: ${emissionId}`);
      }
      activeAttack.activeParticleEmissions = [];
    }
    
    // Cleanup any remaining channel lights via effectsController
    if (activeAttack.activeChannelLights) {
      this.effectsController.cleanupLights(activeAttack.activeChannelLights);
    }
    
    // Cleanup static FX meshes via effectsController
    if (activeAttack.activeStaticFx) {
      this.effectsController.cleanupStaticFx(activeAttack.activeStaticFx);
    }
    
    const endContext: ChannelEndContext = {
      attackerId: attacker.getId(),
      attack: activeAttack.attackDef,
      reason,
      duration,
      tickCount: activeAttack.tickCount ?? 0,
      actions: channelActions,
      state: activeAttack.channelState ?? {}, // OK here since we're ending
    };

    if (activeAttack.attackDef.onChannelEnd) {
      try {
        activeAttack.attackDef.onChannelEnd(endContext);
      } catch (error) {
        logger.core.error(`CombatSystem: onChannelEnd hook threw for ${attacker.getId()}`, error);
      }
    }

    const phase = attacker.animationController?.getCurrentPhase?.() ?? null;
    this.endAttack(attacker.getId(), {
      completed: reason === 'completed',
      reason: reason === 'completed' ? undefined : reason,
      phase,
    });
  }

  /**
   * Build context object for channel presets
   */
  private buildPresetContext(
    attacker: Character,
    activeAttack: ActiveAttack,
    actions: ChannelActions,
    config: Record<string, unknown>,
    channelDuration: number,
    tickCount: number
  ): ChannelPresetContext {
    const maxDuration = activeAttack.attackDef.channeled?.maxDuration;
    const normalizedTime = maxDuration ? Math.min(1, channelDuration / maxDuration) : 0;
    const holdCheck = activeAttack.isHeld;
    
    // Ensure channelState exists and use SAME reference (don't create new object!)
    if (!activeAttack.channelState) {
      activeAttack.channelState = {};
    }
    
    return {
      attack: activeAttack.attackDef,
      attackerId: attacker.getId(),
      channelDuration,
      tickCount,
      buttonHeld: holdCheck ? holdCheck() : true,
      maxDuration,
      normalizedTime,
      actions,
      config,
      state: activeAttack.channelState, // Use existing reference!
    };
  }

  cancelAttackFor(attacker: Character, reason: InterruptReason): boolean {
    const attackerId = attacker.getId();
    const activeAttack = this.activeAttacks.get(attackerId);
    if (!activeAttack) return false;

    const policy = activeAttack.attackDef.interruptionPolicy ?? { allowDamageInterrupt: true, allowPlayerCancel: false };
    const phase = attacker.animationController?.getCurrentPhase?.() ?? null;

    let canInterrupt = true;
    if (reason === 'damage' && policy.allowDamageInterrupt === false) {
      canInterrupt = false;
    }
    if (reason === 'player' && policy.allowPlayerCancel === false) {
      canInterrupt = false;
    }
    if (policy.allowedPhases && phase && !policy.allowedPhases.includes(phase)) {
      canInterrupt = false;
    }

    const context: InterruptContext = {
      attackerId,
      attack: activeAttack.attackDef,
      reason,
      phase,
      canInterrupt,
    };

    if (activeAttack.attackDef.onInterruptCheck) {
      try {
        const decision = activeAttack.attackDef.onInterruptCheck(context);
        if (typeof decision === 'boolean') {
          context.canInterrupt = decision;
        }
      } catch (error) {
        logger.core.error(`CombatSystem: onInterruptCheck hook threw for ${attackerId}`, error);
      }
    }

    if (!context.canInterrupt) {
      return false;
    }

    if ((activeAttack.type ?? activeAttack.attackDef.type) === 'channeled') {
      this.endChanneledAttack(attacker, activeAttack, reason);
    } else {
      this.endAttack(attackerId, { completed: false, reason, phase });
    }

    return true;
  }

  /**
   * Get current game time in seconds (affected by time scale).
   * This is accumulated from deltaTime passed to update(), not wall-clock time.
   */
  private getTimeSeconds(): number {
    return this._gameTimeElapsed;
  }

  private async ensureAttachedMeshes(attacker: Character, activeAttack: ActiveAttack): Promise<void> {
    if (activeAttack.attachedMeshInstances || !activeAttack.attackDef.channeled?.attachedMeshes?.length) {
      return;
    }

    const specs = activeAttack.attackDef.channeled.attachedMeshes;
    if (!specs) return;

    const instances = await this.effectsController.loadAttachedMeshes(attacker, specs);
    if (instances.length > 0) {
      activeAttack.attachedMeshInstances = instances;
    }
  }

  private cleanupAttachedMeshes(activeAttack: ActiveAttack): void {
    if (!activeAttack.attachedMeshInstances) return;
    this.effectsController.cleanupAttachedMeshes(activeAttack.attachedMeshInstances);
    activeAttack.attachedMeshInstances = undefined;
  }

  /**
   * Request an attack from a character.
   * Spawns weapon/projectile instances and tracks them.
   */
  startAttack(attacker: Character, attackDef: AttackDef, targetPosition?: Vector3, options?: AttackRequestOptions): void {
    console.log(`⚔️ CombatSystem.startAttack called: ${attacker.getId()} starting attack ${attackDef.id}, events=${attackDef.events?.length ?? 0}`);
    logger.core.debug(`CombatSystem: ${attacker.getId()} starting attack ${attackDef.id}`);

    const attackerId = attacker.getId();
    
    const existingAttack = this.activeAttacks.get(attackerId);
    if (existingAttack) {
      // Default behavior: do NOT allow starting a new attack while one is active.
      // Only cancel/replace if the active attack explicitly allows player cancel.
      const canceled = this.cancelAttackFor(attacker, 'player');
      if (!canceled) {
        logger.core.debug(
          `CombatSystem: Ignoring attack '${attackDef.id}' for ${attackerId} because '${existingAttack.attackDef.id}' is still active and not player-cancelable`
        );
        return;
      }
    }

    // First, check with XState if the attack can proceed (cooldown, guard checks).
    // sendCharacterEvent returns true if a transition occurred, false if guard rejected.
    const stateTransitioned = StateManager.getInstance().sendCharacterEvent(attackerId, {
      type: 'INPUT_ATTACK',
      attackId: attackDef.id,
      attackDef: attackDef,
    });
    
    if (!stateTransitioned) {
      console.log(`⚔️ CombatSystem: Attack '${attackDef.id}' for ${attackerId} REJECTED by state machine`);
      return; // Don't play animation or create active attack if state machine rejected
    }
    
    console.log(`⚔️ CombatSystem: Attack '${attackDef.id}' for ${attackerId} ACCEPTED by state machine`);

    // Create active attack tracking
    const attackType = attackDef.type ?? 'oneshot';

    // Capture facing direction at attack start for consistent projectile aiming
    // On curved splines, the tangent changes as character moves - we want to shoot
    // in the direction we were facing when we initiated the attack
    const startDirection = attacker.getForwardDirection().clone();
    // Flatten to horizontal for projectiles
    startDirection.y = 0;
    if (startDirection.lengthSquared() > 1e-4) {
      startDirection.normalize();
    } else {
      startDirection.set(0, 0, 1);
    }

    const activeAttack: ActiveAttack = {
      attackerId,
      attackDef,
      startTime: this.getTimeSeconds(),
      startDirection,
      processedEvents: new Set(),
      type: attackType,
    };

    if (options?.isHeld) {
      activeAttack.isHeld = options.isHeld;
    }
    if (options?.binding) {
      activeAttack.binding = options.binding;
    }
    
    this.activeAttacks.set(attackerId, activeAttack);
    console.log(`⚔️ CombatSystem: Added activeAttack for ${attackerId}, activeAttacks.size=${this.activeAttacks.size}, events=${attackDef.events?.map(e => e.type).join(',') ?? 'none'}`);

    this.prepareProjectilePreview(attacker, activeAttack);
    
    // Spawn windup-phase static FX immediately (before channel starts)
    if (attackDef.channeled?.presets) {
      this.spawnWindupStaticFx(attacker, activeAttack, attackDef.channeled.presets);
    }

    const attackStartCallback = attacker.getAttackStartCallback();
    if (attackStartCallback) {
      const context: CharacterAttackContext = {
        attackerId,
        attack: attackDef,
        targetIds: targetPosition ? [] : []
      };
      try {
        attackStartCallback(context);
      } catch (error) {
        logger.core.error(`CombatSystem: onAttackStart callback threw for ${attackerId}`, error);
      }
    }

    // Trigger the attack animation on the character.
    // This used to be driven by the legacy AttackState; CombatSystem is now the owner.
    const animController = attacker.animationController;
    if (animController) {
      if (attackType === 'channeled' && attackDef.channeled?.loopAnimation) {
        // Check if the animation has a loopRange defined (for intro/loop/outro)
        const hasLoopRange = animController.getAnimationMetadata(attackDef.channeled.loopAnimation)?.loopRange;
        if (hasLoopRange) {
          animController.playLoopingWithRange(attackDef.channeled.loopAnimation);
        } else {
          animController.playLooping(attackDef.channeled.loopAnimation);
        }
      } else {
        // Determine if we should apply the mask based on current animation state
        let effectiveMaskBones = attackDef.maskBones;
        let disableRootMotionForMask = false;
        let speedRatio = 1.0;
        
        const shouldCheckStates = attackDef.applyMaskWhenInStates?.length && attackDef.maskBones?.length;
        
        if (shouldCheckStates) {
          // Check if current state is in the list of states that trigger masking
          const currentState = animController.getCurrentState();
          const shouldApplyMask = attackDef.applyMaskWhenInStates!.includes(currentState as string);
          
          console.log(`[CombatSystem] currentState='${currentState}', applyMaskWhenInStates=${JSON.stringify(attackDef.applyMaskWhenInStates)}, shouldApplyMask=${shouldApplyMask}`);
          
          if (!shouldApplyMask) {
            // Current state not in mask-trigger list - no mask, full body animation
            effectiveMaskBones = undefined;
            speedRatio = attackDef.speedWhenUnmasked ?? 1.0;
            console.log(`[CombatSystem] Full-body attack, speedRatio=${speedRatio}`);
            logger.core.debug(`CombatSystem: ${attackerId} in state '${currentState}', playing full-body attack (speed: ${speedRatio})`);
          } else {
            // Current state triggers masking
            disableRootMotionForMask = attackDef.disableRootMotionWhenMasked ?? true; // Default to true when masked
            speedRatio = attackDef.speedWhenMasked ?? 1.0;
            console.log(`[CombatSystem] Masked attack, speedRatio=${speedRatio}`);
            logger.core.debug(`CombatSystem: ${attackerId} in state '${currentState}', playing upper-body attack (masked, speed: ${speedRatio})`);
          }
        } else if (effectiveMaskBones?.length) {
          // Always masked - check if root motion should be disabled
          disableRootMotionForMask = attackDef.disableRootMotionWhenMasked ?? false;
        }
        
        // Build blend config
        const blendConfig = {
          blendIn: attackDef.blendIn,
          blendOut: attackDef.blendOut,
          disableRootMotion: disableRootMotionForMask
        };
        
        animController.playOneShot(attackDef.animation, undefined, speedRatio, effectiveMaskBones, blendConfig);

        const isMaskedAttack = !!(effectiveMaskBones?.length);
        if (attackType === 'oneshot' && !isMaskedAttack) {
          const group = (animController as any).activeGroup as AnimationGroup | null;
          if (group) {
            const metadata = ((group as any).metadata ??= {});
            const previous = metadata.exitTime as { enabled: boolean; time: number } | undefined;

            activeAttack.lockedExitTimeGroup = group;
            activeAttack.previousExitTimeConfig = previous ? { ...previous } : undefined;

            // Force animation to play to completion (1.0) to prevent locomotion from interrupting
            metadata.exitTime = { enabled: true, time: 1.0 };
          }
        }
      }
    } else {
      logger.core.warn(`CombatSystem: ${attackerId} has no animationController; cannot play '${attackDef.animation}'`);
    }

    // Emit attack start event
    this.eventBus.emit('combat:attack-start', {
      attackerId,
      attackDefId: attackDef.id,
      targetPosition,
    } as CombatAttackStartEvent);

    logger.core.log(`[CombatSystem] Attack STARTED for ${attackerId} (${attackDef.id})`);
    logger.core.debug(`CombatSystem: Active attack created for ${attackerId}`);
  }

  hasActiveAttack(attackerId: string): boolean {
    return this.activeAttacks.has(attackerId);
  }

  /**
   * End an active attack and clean up resources.
   */
  private endAttack(
    attackerId: string,
    options?: { completed?: boolean; reason?: InterruptReason; phase?: any; bufferedInput?: string | null }
  ): void {
    const activeAttack = this.activeAttacks.get(attackerId);
    if (!activeAttack) return;

    if (activeAttack.lockedExitTimeGroup) {
      const metadata = ((activeAttack.lockedExitTimeGroup as any).metadata ??= {});
      if (activeAttack.previousExitTimeConfig) {
        metadata.exitTime = { ...activeAttack.previousExitTimeConfig };
      } else {
        delete metadata.exitTime;
      }
    }

    const completed = options?.completed !== false;
    const phase = options?.phase ?? null;
    const attackDef = activeAttack.attackDef;

    const attackEndContext: AttackEndContext = {
      attackerId,
      attack: attackDef,
      completed,
      phase,
      bufferedInput: options?.bufferedInput,
    };

    if (!completed && attackDef.onAttackCancel && options?.reason) {
      const cancelContext = {
        attackerId,
        attack: attackDef,
        reason: options.reason,
        phase,
      } as const;
      try {
        attackDef.onAttackCancel(cancelContext);
      } catch (error) {
        logger.core.error(`CombatSystem: onAttackCancel hook threw for ${attackerId}`, error);
      }
    }

    if (attackDef.onAttackEnd) {
      try {
        attackDef.onAttackEnd(attackEndContext);
      } catch (error) {
        logger.core.error(`CombatSystem: onAttackEnd hook threw for ${attackerId}`, error);
      }
    }

    this.cleanupAttachedMeshes(activeAttack);
    this.cleanupPreparedProjectile(activeAttack);
    
    // Send ANIM_COMPLETE to XState if attack completed successfully (Sprint 0023)
    if (completed) {
      StateManager.getInstance().sendCharacterEvent(attackerId, {
        type: 'ANIM_COMPLETE',
        animationName: attackDef.animation,
      });
      logger.core.debug(`[CombatSystem] Sent ANIM_COMPLETE for attack '${attackDef.animation}'`);
    }

    // Conditionally disable weapon mesh based on keepWeaponAttached setting
    if (activeAttack.weaponInstance) {
      // Never hide submesh weapons - they should always remain visible
      // Only hide dynamically spawned weapons or embedded weapons if keepWeaponAttached is false
      const isSubmeshWeapon = activeAttack.weaponInstance.isFromSubmesh === true;
      const shouldKeepAttached = isSubmeshWeapon || (attackDef.keepWeaponAttached ?? (attackDef.weaponAssetPath !== undefined));
      
      if (!shouldKeepAttached) {
        // For embedded weapons, hide the entire entity root
        const entityRoot = (activeAttack.weaponInstance as any).entityRoot as TransformNode | undefined;
        if (entityRoot) {
          entityRoot.setEnabled(false);
          if (entityRoot instanceof AbstractMesh) {
            (entityRoot as AbstractMesh).isVisible = false;
          }
          for (const child of entityRoot.getChildMeshes(false)) {
            child.setEnabled(false);
            child.isVisible = false;
          }
        } else {
          activeAttack.weaponInstance.weaponMesh.setEnabled(false);
          activeAttack.weaponInstance.weaponMesh.isVisible = false;
        }
      }
      
      // Disable hitbox regardless
      activeAttack.weaponInstance.hitboxEnabled = false;
      
      // Remove from activeWeapons tracking (safe now that hitbox is disabled)
      this.activeWeapons.delete(activeAttack.weaponInstance.id);
      
      // Only dispose collision mesh if it exists and was dynamically created (NOT for embedded)
      // Embedded collision meshes are part of the entity and should be reused
      if (activeAttack.weaponInstance.collisionMesh && !activeAttack.weaponInstance.isFromSubmesh && !(activeAttack.weaponInstance as any).entityRoot) {
        activeAttack.weaponInstance.collisionMesh.dispose();
      }
    }

    this.activeAttacks.delete(attackerId);
    
    // Notify XState actor of attack end (Sprint 0023)
    StateManager.getInstance().sendCharacterEvent(attackerId, {
      type: 'ATTACK_END',
      completed: options?.completed !== false,
    });
    
    const attackDurationMs = ((performance.now() / 1000) - (activeAttack.startTime ?? 0)) * 1000;
    logger.core.log(`[CombatSystem] Attack ended for ${attackerId} (${attackDef.id}, completed=${completed}, duration=${attackDurationMs.toFixed(0)}ms, activeAttacks=${this.activeAttacks.size})`);
  }

  private prepareProjectilePreview(attacker: Character, activeAttack: ActiveAttack): void {
    const spec = activeAttack.attackDef.projectile;
    if (!spec) {
      this.cleanupPreparedProjectile(activeAttack);
      return;
    }

    if (activeAttack.preparedProjectile) {
      return;
    }

    const characterAsset = (attacker as any).characterAsset as CharacterAsset | undefined;
    const embeddedEntities = characterAsset?.embeddedEntities;
    
    // Log diagnostic info for embedded projectile debugging
    if (spec.assetPath && isEmbeddedReference(spec.assetPath)) {
      logger.core.log(`🎯 Preparing projectile from embedded ref: ${spec.assetPath}`);
      logger.core.log(`   characterAsset exists: ${!!characterAsset}`);
      logger.core.log(`   embeddedEntities exists: ${!!embeddedEntities}`);
      if (embeddedEntities) {
        logger.core.log(`   Available entities: [${Array.from(embeddedEntities.entities.keys()).join(', ')}]`);
      }
    }
    
    const previewMesh = createProjectileMesh(
      this.scene,
      spec, 
      `projectile_preview_${activeAttack.attackerId}_${Date.now()}`,
      embeddedEntities
    );
    
    // For channeled attacks, hide the preview - we spawn copies on each tick
    // For oneshot attacks, show the preview attached to the character
    const isChanneled = activeAttack.attackDef.channeled !== undefined;
    previewMesh.isVisible = !isChanneled;
    previewMesh.isPickable = false;
    previewMesh.checkCollisions = false;
    
    // Also hide all child meshes for channeled attacks
    if (isChanneled) {
      previewMesh.getChildMeshes(false).forEach(child => {
        child.isVisible = false;
      });
    }

    const rootMesh: AbstractMesh | undefined = characterAsset?.rootMesh;
    const skeleton: Skeleton | undefined = characterAsset?.skeleton ?? undefined;

    let attached = false;
    const muzzleBone = spec.muzzleBone;
    if (muzzleBone && skeleton && rootMesh) {
      const bone = skeleton.bones.find((b: Bone) => b.name === muzzleBone);
      if (bone) {
        previewMesh.attachToBone(bone, rootMesh);
        previewMesh.position.set(0, 0, 0);
        previewMesh.rotationQuaternion = null;
        previewMesh.rotation.set(0, 0, 0);
        activeAttack.preparedProjectile = { mesh: previewMesh, boneName: muzzleBone };
        attached = true;
      } else {
        logger.core.warn(`CombatSystem: muzzleBone '${muzzleBone}' not found for ${activeAttack.attackerId}`);
      }
    }

    if (!attached) {
      if (rootMesh) {
        previewMesh.parent = rootMesh;
      } else {
        previewMesh.parent = attacker.character;
      }
      previewMesh.position.set(0, 0, 0);
      activeAttack.preparedProjectile = { mesh: previewMesh, boneName: muzzleBone };
    }
  }

  private cleanupPreparedProjectile(activeAttack: ActiveAttack): void {
    const prepared = activeAttack.preparedProjectile;
    if (!prepared) {
      return;
    }

    const { mesh } = prepared;
    if (mesh) {
      const detach = (mesh as unknown as { detachFromBone?: () => void }).detachFromBone;
      if (typeof detach === 'function') {
        detach.call(mesh);
      } else {
        mesh.parent = null;
      }
      mesh.dispose();
    }

    delete activeAttack.preparedProjectile;
  }

  /**
   * Spawn static FX that should appear during windup phase (before channel starts).
   * These are FX defined in presets with spawnPhase: 'windup'.
   */
  private spawnWindupStaticFx(
    attacker: Character,
    activeAttack: ActiveAttack,
    presets: ChannelPreset[]
  ): void {
    // Collect all staticFxConfig entries with spawnPhase === 'windup'
    const windupFxConfigs: Array<{
      id?: string;
      assetPath: string;
      boneName?: string;
      scale?: number;
      offset?: { x: number; y: number; z: number };
      orientationMode?: 'bone' | 'world' | 'facing';
      forward?: { x: number; y: number; z: number };
      up?: { x: number; y: number; z: number };
      localRotation?: { x?: number; y?: number; z?: number };
      playbackRate?: number;
      videoLoopRange?: { start: number; end: number };
    }> = [];

    for (const preset of presets) {
      if (preset.staticFxConfig) {
        for (const fxConfig of preset.staticFxConfig) {
          if (fxConfig.spawnPhase === 'windup') {
            windupFxConfigs.push(fxConfig);
          }
        }
      }
    }

    if (windupFxConfigs.length === 0) {
      return;
    }

    if (!activeAttack.activeStaticFx) {
      activeAttack.activeStaticFx = [];
    }

    const embeddedEntities = this.getEmbeddedEntitiesForCharacter(attacker);
    const characterAsset = (attacker as any).characterAsset as CharacterAsset | undefined;
    const skeleton = characterAsset?.skeleton;
    const rootMesh = characterAsset?.rootMesh;

    for (const fxConfig of windupFxConfigs) {
      try {
        // Create a minimal ProjectileSpec for the static FX
        const fxSpec = {
          assetId: fxConfig.id ?? `windupFx_${Date.now()}`,
          assetPath: fxConfig.assetPath,
          type: 'static' as const,
          speed: 0,
          directionMode: 'bone' as const,
          scale: fxConfig.scale ?? 1,
        };

        const fxMesh = createProjectileMesh(
          this.scene,
          fxSpec,
          `windupFx_${fxConfig.id ?? Date.now()}`,
          embeddedEntities
        );

        if (fxMesh) {
          const boneName = fxConfig.boneName ?? 'mixamorig:RightHand';
          const bone = skeleton?.bones.find((b: Bone) => b.name === boneName);
          const orientationMode = fxConfig.orientationMode ?? 'bone';

          if (bone && rootMesh) {
            fxMesh.attachToBone(bone, rootMesh);

            if (fxConfig.offset) {
              fxMesh.position.set(fxConfig.offset.x, fxConfig.offset.y, fxConfig.offset.z);
            } else {
              fxMesh.position.set(0, 0, 0);
            }

            // Apply orientation based on mode
            if (orientationMode === 'world') {
              fxMesh.billboardMode = AbstractMesh.BILLBOARDMODE_NONE;
              const forward = fxConfig.forward
                ? new Vector3(fxConfig.forward.x, fxConfig.forward.y, fxConfig.forward.z)
                : new Vector3(0, 0, 1);
              const up = fxConfig.up
                ? new Vector3(fxConfig.up.x, fxConfig.up.y, fxConfig.up.z)
                : new Vector3(0, 1, 0);
              (fxMesh as any)._worldOrientation = { forward, up };
            } else if (orientationMode === 'facing') {
              const forward = fxConfig.forward
                ? new Vector3(fxConfig.forward.x, fxConfig.forward.y, fxConfig.forward.z)
                : new Vector3(0, 0, 1);
              const up = fxConfig.up
                ? new Vector3(fxConfig.up.x, fxConfig.up.y, fxConfig.up.z)
                : new Vector3(0, 1, 0);
              (fxMesh as any)._facingOrientation = { forward, up };
            }
          } else if (rootMesh) {
            fxMesh.parent = rootMesh;
          }

          // Apply additional local rotation if specified
          if (fxConfig.localRotation) {
            const degToRad = Math.PI / 180;
            fxMesh.rotation.x += (fxConfig.localRotation.x ?? 0) * degToRad;
            fxMesh.rotation.y += (fxConfig.localRotation.y ?? 0) * degToRad;
            fxMesh.rotation.z += (fxConfig.localRotation.z ?? 0) * degToRad;
          }

          fxMesh.setEnabled(true);
          fxMesh.isVisible = true;

          // Apply per-instance video settings override if specified
          const needsCustomVideo =
            (fxConfig.playbackRate !== undefined && fxConfig.playbackRate !== 1.0) ||
            fxConfig.videoLoopRange !== undefined;
          if (needsCustomVideo) {
            const applyVideoOverride = (mesh: AbstractMesh) => {
              const existingVideoTexture = mesh.metadata?.hmVideoTexture;
              if (existingVideoTexture?.video) {
                const videoSrc = existingVideoTexture.video.src;
                if (videoSrc) {
                  const scene = mesh.getScene();
                  const newTexture = createVideoTextureInstance(
                    scene,
                    videoSrc,
                    true,
                    true,
                    fxConfig.playbackRate ?? 1.0,
                    fxConfig.videoLoopRange
                  );
                  if (mesh.material) {
                    applyVideoToMaterial(mesh.material, newTexture, 1.0, 'additive');
                  }
                  mesh.metadata.hmVideoTexture = newTexture;
                  mesh.metadata.hmVideoTextureOwned = true;
                }
              }
              mesh.getChildMeshes?.().forEach(applyVideoOverride);
            };
            applyVideoOverride(fxMesh);
          }

          // Store with ID for later cleanup
          (fxMesh as any)._staticFxId = fxConfig.id;
          (fxMesh as any)._orientationMode = orientationMode;
          activeAttack.activeStaticFx.push(fxMesh);
          logger.combat.debug(
            `Spawned windup static FX '${fxConfig.assetPath}' (id=${fxConfig.id}) attached to bone '${boneName}' mode='${orientationMode}'`
          );
        }
      } catch (error) {
        logger.combat.error(`Failed to spawn windup static FX '${fxConfig.assetPath}':`, error);
      }
    }
  }

  private resolveProjectileLaunch(
    attacker: Character,
    activeAttack: ActiveAttack
  ): ProjectileLaunchParams | null {
    const spec = activeAttack.attackDef.projectile;
    if (!spec) {
      logger.core.warn(`CombatSystem: Attack ${activeAttack.attackDef.id} missing projectile spec during handover`);
      return null;
    }

    const prepared = activeAttack.preparedProjectile;
    let attachmentPosition: Vector3;
    let attachmentForward: Vector3;
    let attachmentUp: Vector3;
    let attachmentRight: Vector3;

    if (prepared) {
      attachmentPosition = prepared.mesh.getAbsolutePosition().clone();
      attachmentForward = prepared.mesh.getDirection(Vector3.Forward()).normalize();
      attachmentUp = prepared.mesh.getDirection(Vector3.Up()).normalize();
      attachmentRight = prepared.mesh.getDirection(Vector3.Right()).normalize();
    } else {
      attachmentPosition = attacker.character.getAbsolutePosition().clone();
      attachmentForward = attacker.character.getDirection(Vector3.Forward()).normalize();
      attachmentUp = attacker.character.getDirection(Vector3.Up()).normalize();
      attachmentRight = attacker.character.getDirection(Vector3.Right()).normalize();
    }

    // Use getForwardDirection() which returns the intended facing direction
    // This is correct even during turn animations when mesh rotation is locked
    const characterForward = attacker.getForwardDirection().normalize();
    const characterVelocity = attacker.velocity.clone();
    const characterPosition = attacker.character.getAbsolutePosition().clone();

    let defaultDirection: Vector3;
    switch (spec.directionMode) {
      case 'bone':
        defaultDirection = attachmentForward.clone();
        break;
      case 'aim':
        // TODO: Implement aim-based direction selection when aim targets exist.
        defaultDirection = attachmentForward.clone();
        break;
      case 'facing':
      default:
        // Use the facing direction captured at attack start for consistent aiming.
        // On curved splines, the tangent changes as the character moves - using the
        // start direction ensures projectiles go where the player was aiming.
        if (activeAttack.startDirection) {
          defaultDirection = activeAttack.startDirection.clone();
        } else {
          // Fallback: use current horizontal forward direction
          defaultDirection = new Vector3(characterForward.x, 0, characterForward.z);
        }
        break;
    }

    if (defaultDirection.lengthSquared() < 1e-4) {
      defaultDirection = new Vector3(0, 0, 1);
    }

    defaultDirection.normalize();
    const defaultSpeed = spec.speed;
    const defaultVelocity = defaultDirection.scale(defaultSpeed);

    const defaults = {
      position: attachmentPosition.clone(),
      direction: defaultDirection.clone(),
      speed: defaultSpeed,
      velocity: defaultVelocity.clone(),
    };

    const context: ProjectileHandoverContext = {
      attackerId: attacker.getId(),
      attack: activeAttack.attackDef,
      spec,
      attachment: {
        boneName: prepared?.boneName ?? spec.muzzleBone,
        position: attachmentPosition.clone(),
        forward: attachmentForward.clone(),
        up: attachmentUp.clone(),
        right: attachmentRight.clone(),
      },
      character: {
        position: characterPosition.clone(),
        velocity: characterVelocity.clone(),
        forward: characterForward.clone(),
      },
      defaults,
    };

    let directive: ProjectileLaunchDirective | void = undefined;
    const hook = activeAttack.attackDef.onProjectileHandover;
    if (hook) {
      try {
        directive = hook(context);
      } catch (error) {
        logger.core.error(`CombatSystem: onProjectileHandover hook threw for ${activeAttack.attackerId}`, error);
      }
    }

    let launchPosition = defaults.position.clone();
    let launchDirection = defaults.direction.clone();
    let launchSpeed = defaults.speed;
    let launchVelocity = defaults.velocity.clone();

    if (directive) {
      if (directive.position) {
        launchPosition = directive.position.clone();
      }

      if (directive.velocity) {
        launchVelocity = directive.velocity.clone();
        const velocityLength = launchVelocity.length();
        if (velocityLength > 1e-4) {
          launchDirection = launchVelocity.clone().normalize();
          launchSpeed = velocityLength;
        }
      } else {
        if (directive.direction) {
          const dir = directive.direction.clone();
          if (dir.lengthSquared() > 1e-4) {
            launchDirection = dir.normalize();
          }
        }
        if (typeof directive.speed === 'number' && Number.isFinite(directive.speed)) {
          launchSpeed = directive.speed;
        }
        launchVelocity = launchDirection.clone().scale(launchSpeed);
      }
    }

    return {
      position: launchPosition,
      velocity: launchVelocity,
      direction: launchDirection,
      mesh: null,
    };
  }

  /**
   * Process attack animation events based on normalized time.
   */
  private processAttackEvents(
    activeAttack: ActiveAttack,
    attacker: Character,
    normalizedTime: number
  ): void {
    const events = activeAttack.attackDef.events;
    if (!events || events.length === 0) {
      logger.combat.debug(`No events for attack ${activeAttack.attackDef.id}`);
      return;
    }

    // Log once per attack for debugging
    if (!activeAttack.processedEvents.has(-1)) {
      console.log(`⚔️ processAttackEvents FIRST CALL: ${attacker.getId()}, attack=${activeAttack.attackDef.id}, events=${events.length}, normalizedTime=${normalizedTime.toFixed(3)}`);
      activeAttack.processedEvents.add(-1); // Marker to prevent re-logging
    }

    // Process events that should have fired by current normalized time
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      
      // Skip if already processed
      if (activeAttack.processedEvents.has(i)) continue;
      
      // Check if event time has been reached
      if (normalizedTime >= event.time) {
        logger.combat.debug(`Processing event ${event.type} at t=${event.time} (current=${normalizedTime.toFixed(3)}) for ${attacker.getId()}`);
        logger.combat.debug(
          `CombatSystem: Processing event ${event.type} at t=${event.time} for ${attacker.getId()}`
        );
        
        // Process the event
        switch (event.type) {
          case 'attachWeapon': {
            // Find existing weapon mesh in character hierarchy (don't spawn new one)
            const characterAsset = (attacker as any).characterAsset;
            logger.core.log(`⚔️ attachWeapon event: characterAsset exists: ${!!characterAsset}`);
            
            if (characterAsset) {
              logger.core.log(`⚔️ characterAsset.meshes count: ${characterAsset.meshes?.length || 0}`);
              const submeshCount = characterAsset.submeshes instanceof Map
                ? characterAsset.submeshes.size
                : Array.isArray(characterAsset.submeshes)
                  ? characterAsset.submeshes.length
                  : 0;
              logger.core.log(`⚔️ characterAsset.submeshes count: ${submeshCount}`);
              
              // Log all mesh names for debugging
              if (characterAsset.meshes) {
                logger.core.log(`⚔️ Available meshes: ${characterAsset.meshes.map((m: any) => m.name).join(', ')}`);
              }
              
              const weaponResult = this.findWeaponMeshInCharacter(characterAsset, activeAttack.attackDef, event.params);
              if (weaponResult) {
                const { weaponMesh, entityRoot, collisionMesh, attachBone, isEmbedded } = weaponResult;
                
                // For embedded weapons, we need to:
                // 1. Attach the entity root to the bone (if not already attached)
                // 2. Enable and show the weapon meshes
                if (isEmbedded && entityRoot) {
                  // Enable the entity root and all children
                  entityRoot.setEnabled(true);
                  if (entityRoot instanceof AbstractMesh) {
                    (entityRoot as AbstractMesh).isVisible = true;
                  }
                  for (const child of entityRoot.getChildMeshes(false)) {
                    child.setEnabled(true);
                    // Only show render meshes, not collision meshes
                    const extras = child.metadata?.gltf?.extras;
                    const isCollision = extras?.hm_collision === true || child.metadata?.hm_collision === true;
                    child.isVisible = !isCollision;
                  }
                  
                  // Attach to bone if specified and not already attached
                  if (attachBone && characterAsset.skeleton) {
                    const skeleton: Skeleton = characterAsset.skeleton;
                    const bone = skeleton.bones.find((b: Bone) => b.name === attachBone);
                    if (bone) {
                      // Check if already attached to this bone
                      const alreadyAttached = (entityRoot as any)._attachedBone === bone;
                      if (!alreadyAttached) {
                        // CRITICAL: Use rootMesh (the skinned mesh/armature root), NOT attacker.character (physics capsule)
                        const skinnedMesh = characterAsset.rootMesh;
                        if (!skinnedMesh) {
                          logger.core.warn(`⚔️ No rootMesh on characterAsset for bone attachment`);
                        }
                        
                        // Create anchor TransformNode and attach to bone
                        // This is the same pattern as CharacterLoader for proper bone attachment
                        const anchor = new TransformNode(`${entityRoot.name}_bone_anchor`, this.scene);
                        anchor.attachToBone(bone, skinnedMesh);
                        
                        // Normalize bone scaling on anchor to counteract bone scale
                        const boneMatrix = bone.getAbsoluteTransform();
                        const boneScale = new Vector3();
                        boneMatrix.decompose(boneScale, undefined, undefined);
                        if (boneScale.lengthSquared() > 0.0001) {
                          const normalizationFactor = new Vector3(1/boneScale.x, 1/boneScale.y, 1/boneScale.z);
                          anchor.scaling.copyFrom(normalizationFactor);
                        }
                        
                        // Parent weapon root to anchor (not directly to bone)
                        entityRoot.parent = anchor;
                        
                        // Reset to bone origin - weapon follows bone transform exactly
                        entityRoot.position.set(0, 0, 0);
                        entityRoot.rotationQuaternion = null;
                        entityRoot.rotation.set(0, 0, 0);
                        entityRoot.scaling.setAll(1); // Reset scale, anchor handles bone scale normalization
                        
                        (entityRoot as any)._attachedBone = bone; // Mark as attached
                        (entityRoot as any)._boneAnchor = anchor; // Store anchor for cleanup
                        logger.core.log(`⚔️ Attached weapon '${entityRoot.name}' to bone '${attachBone}' via anchor at origin (skinnedMesh: ${skinnedMesh?.name})`);
                      } else {
                        logger.core.log(`⚔️ Weapon already attached to bone '${attachBone}'`);
                      }
                    } else {
                      const availableBones = skeleton.bones.map((b: Bone) => b.name).slice(0, 10).join(', ');
                      logger.core.warn(`⚔️ Bone '${attachBone}' not found. Available: ${availableBones}...`);
                    }
                  } else if (!attachBone) {
                    logger.core.warn(`⚔️ No attach bone specified for embedded weapon`);
                  }
                } else {
                  // Submesh weapons are already attached, just enable/show
                  weaponMesh.setEnabled(true);
                  weaponMesh.isVisible = true;
                }
                
                logger.core.log(`⚔️ Weapon ${weaponMesh.name} enabled for ${activeAttack.attackerId}`);
                
                // Track it so we can disable later
                activeAttack.weaponInstance = {
                  id: `weapon_${activeAttack.attackerId}`,
                  ownerId: activeAttack.attackerId,
                  attackDefId: activeAttack.attackDef.id,
                  weaponMesh: weaponMesh,
                  collisionMesh: collisionMesh,
                  hitboxEnabled: false,
                  hasHit: false,
                  normalizedTimeStart: normalizedTime,
                  hasDedicatedCollisionMesh: collisionMesh !== undefined,
                  isFromSubmesh: !isEmbedded, // Only submesh weapons should never be hidden
                };
                
                // Store entity root for cleanup
                if (entityRoot) {
                  (activeAttack.weaponInstance as any).entityRoot = entityRoot;
                }
                
                // Add to active weapons map for tracking and debug visualization
                this.activeWeapons.set(activeAttack.weaponInstance.id, activeAttack.weaponInstance);
                logger.core.log(`⚔️ Weapon added to activeWeapons map: ${activeAttack.weaponInstance.id}, total weapons: ${this.activeWeapons.size}, hasCollisionMesh: ${collisionMesh !== undefined}`);
              } else {
                logger.core.error(`❌ No weapon mesh found in character ${activeAttack.attackerId} (searched for: ${activeAttack.attackDef.weaponAssetPath})`);
                if (characterAsset.submeshes instanceof Map) {
                  const submeshKeys = Array.from(characterAsset.submeshes.keys());
                  logger.core.error(`❌ Available submesh keys: ${submeshKeys.join(', ') || 'none'}`);
                } else if (Array.isArray(characterAsset.submeshes)) {
                  logger.core.error(`❌ Available submesh ids: ${characterAsset.submeshes.map((s: any) => s?.id).join(', ') || 'none'}`);
                } else {
                  logger.core.error(`❌ characterAsset.submeshes is not a Map/Array (type: ${typeof characterAsset.submeshes})`);
                }
              }
            } else {
              logger.core.error(`❌ Character ${activeAttack.attackerId} has no characterAsset!`);
            }
            break;
          }
          
          case 'detachProjectile': {
            const launch = this.resolveProjectileLaunch(attacker, activeAttack);
            const projectile = launch
              ? this.spawnProjectile(attacker, activeAttack.attackDef, launch)
              : null;
            
            if (projectile) {
              activeAttack.projectileInstance = projectile;
              
              // Disable the weapon mesh if it was attached
              if (activeAttack.weaponInstance) {
                activeAttack.weaponInstance.weaponMesh.setEnabled(false);
                activeAttack.weaponInstance.weaponMesh.isVisible = false;
              }
            }

            this.cleanupPreparedProjectile(activeAttack);
            break;
          }
          
          case 'enableHitbox': {
            if (activeAttack.weaponInstance) {
              activeAttack.weaponInstance.hitboxEnabled = true;
              activeAttack.weaponInstance.hasHit = false; // Reset hit flag for new window
              logger.core.debug(`CombatSystem: Hitbox enabled for ${activeAttack.weaponInstance.id}`);
            }
            break;
          }
          
          case 'disableHitbox': {
            if (activeAttack.weaponInstance) {
              activeAttack.weaponInstance.hitboxEnabled = false;
              logger.core.debug(`CombatSystem: Hitbox disabled for ${activeAttack.weaponInstance.id}`);
            }
            break;
          }
          
          case 'playWeaponAnim': {
            if (activeAttack.weaponInstance && activeAttack.weaponInstance.animationGroup) {
              const animName = event.params?.animation || 'attack';
              // TODO: Find and play specific animation
              activeAttack.weaponInstance.animationGroup.play(false);
              logger.core.debug(`CombatSystem: Playing weapon animation ${animName}`);
            }
            break;
          }
        }
        
        // Mark event as processed
        activeAttack.processedEvents.add(i);
      }
    }
  }

  private handlePhaseChange(activeAttack: ActiveAttack, phase: any): void {
    const previous = activeAttack.previousPhase ?? null;
    if (phase === previous) return;
    
    // Notify XState actor of phase change (Sprint 0023)
    if (phase) {
      StateManager.getInstance().sendCharacterEvent(activeAttack.attackerId, {
        type: 'ANIM_PHASE_CHANGE',
        phase: phase,
      });
    }
    
    if (activeAttack.attackDef.onPhaseChange && phase) {
      try {
        activeAttack.attackDef.onPhaseChange(previous, phase);
      } catch (error) {
        logger.core.error(`CombatSystem: onPhaseChange hook threw for ${activeAttack.attackerId}`, error);
      }
    }
    activeAttack.previousPhase = phase;
  }

  private invokeAttackTick(activeAttack: ActiveAttack, phase: any, normalizedTime: number, deltaTime: number): void {
    const hook = activeAttack.attackDef.onAttackTick;
    if (!hook) return;

    const context: AttackTickContext = {
      attackerId: activeAttack.attackerId,
      attack: activeAttack.attackDef,
      normalizedTime,
      phase,
      deltaTime,
    };

    try {
      hook(context);
    } catch (error) {
      logger.core.error(`CombatSystem: onAttackTick hook threw for ${activeAttack.attackerId}`, error);
    }
  }

  /**
   * Find weapon mesh in character's submeshes by config ID.
   * 
   * Weapons must be defined in character config with id: 'weapon' (or similar).
   * This method looks up the submesh by ID, NOT by name patterns.
   */
  private findWeaponMeshInCharacter(
    characterAsset: any,
    _attackDef: AttackDef,
    eventParams?: { bone?: string }
  ): { weaponMesh: AbstractMesh; entityRoot?: TransformNode; collisionMesh?: AbstractMesh; attachBone?: string; isEmbedded: boolean } | null {
    // Look for submesh with id 'weapon' (from character config)
    if (characterAsset.submeshes instanceof Map) {
      const weaponSubmesh = characterAsset.submeshes.get('weapon');
      if (weaponSubmesh?.rootMesh) {
        logger.core.log(`✅ Found weapon via submesh id 'weapon': ${weaponSubmesh.rootMesh.name}`);
        return {
          weaponMesh: weaponSubmesh.rootMesh,
          isEmbedded: false,
        };
      }
      
      // If no 'weapon' submesh, log available submeshes for debugging
      logger.core.log(`🔍 No 'weapon' submesh found in submeshes. Available: ${Array.from(characterAsset.submeshes.keys()).join(', ')}`);
    }
    
    // Check embeddedEntities for weapon type entities
    if (characterAsset.embeddedEntities) {
      const entities = characterAsset.embeddedEntities.entities as Map<string, any>;
      logger.core.log(`⚔️ Checking embeddedEntities for weapon. Found ${entities.size} entities: ${Array.from(entities.keys()).join(', ')}`);
      
      for (const [entityId, entity] of entities) {
        logger.core.log(`⚔️ Entity '${entityId}' type='${entity.type}'`);
        if (entity.type === 'weapon') {
          // Found a weapon entity
          const rootNode = entity.root as TransformNode;
          
          logger.core.log(`⚔️ Found weapon entity '${entityId}', root: ${rootNode.name}, extras: ${JSON.stringify(entity.extras)}`);
          
          // Find the render mesh (non-collision mesh) to return as weaponMesh
          // and find the collision mesh (hm_collision=true) for hit detection
          let weaponMesh: AbstractMesh | null = null;
          let collisionMesh: AbstractMesh | undefined;
          
          for (const mesh of entity.meshes) {
            const extras = mesh.metadata?.gltf?.extras;
            const isCollision = extras?.hm_collision === true || mesh.metadata?.hm_collision === true;
            if (isCollision && mesh instanceof AbstractMesh) {
              collisionMesh = mesh;
              logger.core.log(`⚔️ Found collision mesh: ${mesh.name}`);
            } else if (!isCollision && mesh instanceof AbstractMesh) {
              if (!weaponMesh) {
                weaponMesh = mesh;
                logger.core.log(`⚔️ Found render mesh: ${mesh.name}`);
              }
            }
          }
          
          // If no render mesh found, use root if it's a mesh, or first child mesh
          if (!weaponMesh) {
            if (rootNode instanceof AbstractMesh) {
              weaponMesh = rootNode;
            } else {
              const childMeshes = rootNode.getChildMeshes(false);
              if (childMeshes.length > 0) {
                weaponMesh = childMeshes[0];
              }
            }
            logger.core.log(`⚔️ Using fallback weaponMesh: ${weaponMesh?.name ?? 'none'}`);
          }
          
          // Get attach bone from entity extras or event params
          // Entity extras have hm_ prefix from Blender export
          const entityAttachBone = entity.extras?.hm_attach_bone || entity.extras?.attach_bone;
          const attachBone = eventParams?.bone || entityAttachBone;
          
          logger.core.log(`⚔️ Attach bone: eventParams.bone=${eventParams?.bone}, entityAttachBone=${entityAttachBone}, final=${attachBone}`);
          
          if (weaponMesh) {
            logger.core.log(`✅ Found weapon via embeddedEntities: ${entityId} (mesh: ${weaponMesh.name}, collision: ${collisionMesh?.name ?? 'none'}, bone: ${attachBone ?? 'none'})`);
            return {
              weaponMesh,
              entityRoot: rootNode,
              collisionMesh,
              attachBone,
              isEmbedded: true,
            };
          }
        }
      }
      logger.core.log(`🔍 No weapon entity found in embeddedEntities. Available entities: ${Array.from(entities.keys()).join(', ')}`);
    } else {
      logger.core.log(`🔍 No embeddedEntities on characterAsset`);
    }
    
    logger.core.error(`❌ No weapon found. Character needs either a submesh with id 'weapon' or an embedded entity with hm_behaviors='weapon'.`);
    return null;
  }

  /**
   * Spawn and attach a weapon to a character's bone.
   */
  async spawnWeapon(
    attacker: Character,
    attackDef: AttackDef,
    attachBone: string
  ): Promise<WeaponInstance | null> {
    try {
      // Get weapon asset path from attack config
      const weaponAssetPath = attackDef.weaponAssetPath;
      if (!weaponAssetPath) {
        logger.core.warn(`CombatSystem: No weaponAssetPath in attack ${attackDef.id}`);
        return null;
      }
      
      const weaponScale = attackDef.weaponScale ?? 1.0;
      
      logger.core.debug(`CombatSystem: Spawning weapon for ${attacker.getId()} on bone ${attachBone}`);
      
      // Load weapon asset
      const asset = await SceneLoader.ImportMeshAsync('', '', weaponAssetPath, this.scene);
      
      // Find root mesh (should have hm_weaponRoot=true in metadata)
      let weaponRootMesh: AbstractMesh | null = null;
      for (const mesh of asset.meshes) {
        const metadata = mesh.metadata;
        if (metadata && metadata.gltf && metadata.gltf.extras) {
          if (metadata.gltf.extras.hm_weaponRoot === true) {
            weaponRootMesh = mesh;
            break;
          }
        }
      }
      
      if (!weaponRootMesh) {
        logger.core.warn(`CombatSystem: No hm_weaponRoot found in weapon asset, using first mesh`);
        weaponRootMesh = asset.meshes[0];
      }
      
      // Find collision mesh - use hm_collision annotation only
      let collisionMesh: AbstractMesh | undefined;
      for (const mesh of asset.meshes) {
        const metadata = mesh.metadata;
        if (metadata && metadata.gltf && metadata.gltf.extras) {
          if (metadata.gltf.extras.hm_collision === true) {
            collisionMesh = mesh;
            break;
          }
        }
        // Also check direct metadata for runtime-set properties
        if (metadata?.hm_collision === true) {
          collisionMesh = mesh;
          break;
        }
      }
      
      // Find the bone to attach to
      const characterAsset = (attacker as any).characterAsset;
      if (!characterAsset || !characterAsset.skeleton) {
        logger.core.error(`CombatSystem: Character ${attacker.getId()} has no skeleton`);
        return null;
      }
      
      const skeleton: Skeleton = characterAsset.skeleton;
      const bone: Bone | null = skeleton.bones.find(b => b.name === attachBone) || null;
      
      if (!bone) {
        logger.core.error(`CombatSystem: Bone ${attachBone} not found in character ${attacker.getId()}`);
        return null;
      }
      
      // Create anchor TransformNode and attach to bone
      const anchorId = this.generateInstanceId();
      const anchor = new TransformNode(`weapon_anchor_${anchorId}`, this.scene);
      
      // Attach anchor to bone (Babylon's bone attachment system)
      const characterMesh = attacker.character;
      anchor.attachToBone(bone, characterMesh);
      
      // Parent weapon to anchor
      weaponRootMesh.parent = anchor;
      weaponRootMesh.setEnabled(true);
      
      // Apply scale from config
      weaponRootMesh.scaling.scaleInPlace(weaponScale);
      
      // Create weapon instance
      const weaponInstance: WeaponInstance = {
        id: anchorId,
        ownerId: attacker.getId(),
        attackDefId: attackDef.id,
        weaponMesh: weaponRootMesh,
        collisionMesh,
        attachedBone: attachBone,
        hitboxEnabled: false,
        hasHit: false,
        normalizedTimeStart: 0, // Will be set when attack starts
        animationGroup: asset.animationGroups.find(ag => ag.name.toLowerCase().includes('idle')),
        hasDedicatedCollisionMesh: collisionMesh !== undefined,
      };
      
      this.activeWeapons.set(anchorId, weaponInstance);
      
      // Play idle animation if available
      if (weaponInstance.animationGroup) {
        weaponInstance.animationGroup.play(true);
      }
      
      logger.core.debug(`CombatSystem: Weapon ${anchorId} spawned and attached to ${attachBone}`);
      return weaponInstance;
    } catch (error) {
      logger.core.error(`CombatSystem: Failed to spawn weapon:`, error);
      return null;
    }
  }

  /**
   * Spawn a projectile from a character's weapon/hand.
   */
  spawnProjectile(
    attacker: Character,
    attackDef: AttackDef,
    launchParams: ProjectileLaunchParams
  ): ProjectileInstance | null {
    if (!attackDef.projectile) {
      logger.core.warn(`CombatSystem: Attack ${attackDef.id} has no projectile spec`);
      return null;
    }
    
    const spec = attackDef.projectile;
    
    // Get embedded entities from character's asset cache
    const characterAsset = (attacker as any).characterAsset as CharacterAsset | undefined;
    const embeddedEntities = characterAsset?.embeddedEntities;
    
    // Get damage from attack definition or spec
    const activeAttack = this.activeAttacks.get(attacker.getId());
    const damage = activeAttack?.attackDef?.damage ?? spec.detonationDamage ?? 10;
    
    // Delegate to ProjectileSystem
    const projectileInstance = this.projectileSystem.spawn({
      ownerId: attacker.getId(),
      attackDefId: attackDef.id,
      spec,
      position: launchParams.position,
      velocity: launchParams.velocity,
      direction: launchParams.direction,
      mesh: launchParams.mesh,
      embeddedEntities,
      damage,
      impactFX: attackDef.impactFX, // Pass impact FX config to projectile
    });
    
    if (projectileInstance) {
      logger.core.debug(`CombatSystem: Projectile ${projectileInstance.id} spawned at ${launchParams.position}`);
    }
    
    return projectileInstance;
  }

  /**
   * Update all active weapons and projectiles.
   * Called each frame from main loop after character updates.
   */
  update(deltaTime: number, characters: Character[]): void {
    // Accumulate game time (deltaTime is already scaled by gameTime.timeScale)
    this._gameTimeElapsed += deltaTime;

    // Debug: log active attacks
    if (this.activeAttacks.size > 0) {
      console.log(`⚔️ CombatSystem.update: ${this.activeAttacks.size} active attacks, characters=${characters.map(c => c.getId()).join(',')}`);
    }

    // Process active attacks (check animation events)
    for (const [attackerId, activeAttack] of this.activeAttacks) {
      const attacker = characters.find(c => c.getId() === attackerId);
      if (!attacker) {
        logger.core.warn(`CombatSystem: Attacker ${attackerId} not found, ending attack`);
        this.endAttack(attackerId);
        continue;
      }
      
      // Get animation controller normalized time
      const animController = attacker.animationController as any;
      if (animController && animController.getNormalizedTime) {
        const attackDef = activeAttack.attackDef;
        const attackType = (activeAttack.type ?? attackDef.type ?? 'oneshot') as 'oneshot' | 'channeled';
        const isMaskedAttack = !!(attackDef.maskBones && attackDef.maskBones.length > 0);

        if (attackType === 'oneshot' && !isMaskedAttack && typeof animController.getCurrentState === 'function') {
          const currentState = animController.getCurrentState() as string | null | undefined;
          if (currentState && currentState !== attackDef.animation) {
            logger.core.debug(
              `[CombatSystem] Attack animation overridden for ${attackerId} (${attackDef.id}): currentState='${currentState}', expected='${attackDef.animation}'; ending attack early`
            );
            const phase = animController.getCurrentPhase ? animController.getCurrentPhase() : null;
            this.endAttack(attackerId, { completed: false, reason: 'custom', phase });
            continue;
          }
        }

        const actionNormalizedTime =
          typeof animController.getActionNormalizedTime === 'function'
            ? (animController.getActionNormalizedTime() as number | null)
            : null;

        // IMPORTANT: For masked (action-layer) attacks, do NOT fall back to base-layer time.
        // If the action layer ended/inactivated, we must deterministically end the masked attack.
        if (isMaskedAttack && actionNormalizedTime === null) {
          logger.core.debug(
            `[CombatSystem] Masked attack action layer ended for ${attackerId} (${attackDef.id}); ending attack`
          );

          // Flush any remaining timeline events (e.g., detachProjectile) that may be scheduled late.
          // Without this, action-layer completion can skip events if the actionGroup clears between frames.
          this.processAttackEvents(activeAttack, attacker, 1.0);

          if (attackType === 'channeled') {
            this.endChanneledAttack(attacker, activeAttack, 'completed');
          } else {
            this.endAttack(attackerId, { completed: true, phase: 'recovery' });
          }
          continue;
        }

        const normalizedTime = isMaskedAttack && actionNormalizedTime !== null
          ? actionNormalizedTime
          : animController.getNormalizedTime();
        
        // For masked attacks, manually calculate phase from action normalized time
        // instead of using getCurrentPhase() which returns base layer phase
        let phase: StatePhase | null = null;
        if (attackType === 'channeled') {
          // Channeled attacks use loop range animations with intro/looping/outro phases
          // Map these to combat phases: intro→windup, looping→active, outro→recovery
          const loopPhase = animController.getLoopRangePhase?.();
          if (loopPhase === 'intro') {
            phase = 'windup';
          } else if (loopPhase === 'looping') {
            phase = 'active';
          } else if (loopPhase === 'outro') {
            phase = 'recovery';
          } else {
            // Fallback to normalized time for initial frame before loopRangeState is set
            phase = normalizedTime < 0.3 ? 'windup' : 'active';
          }
        } else if (isMaskedAttack) {
          // Use DEFAULT_ATTACK_PHASES as the standard timing for attacks
          // DEFAULT_ATTACK_PHASES: windup 0-0.3, active 0.3-0.6, recovery 0.6-1.0
          if (normalizedTime < 0.3) {
            phase = 'windup';
          } else if (normalizedTime < 0.6) {
            phase = 'active';
          } else {
            phase = 'recovery';
          }
          logger.core.debug(`[CombatSystem] Masked attack phase: norm=${normalizedTime.toFixed(3)}, phase=${phase}`);
        } else {
          // Non-masked attack: use base layer phase
          phase = animController.getCurrentPhase ? animController.getCurrentPhase() : null;
        }
        
        // Send phase change events to state machine
        this.handlePhaseChange(activeAttack, phase);
        
        // Send RECOVERY_COMPLETE when recovery phase has progressed enough for state transition
        // Only for one-shot attacks - channeled attacks handle completion via ATTACK_END in endChanneledAttack()
        if (attackType !== 'channeled' && phase === 'recovery' && !activeAttack.recoveryCompleteSent) {
          const recoveryExitThreshold = 0.7;
          if (normalizedTime >= recoveryExitThreshold) {
            activeAttack.recoveryCompleteSent = true;
            logger.core.debug(`[CombatSystem] Recovery complete for ${attackerId} at normalizedTime=${normalizedTime.toFixed(3)}`);
            StateManager.getInstance().sendCharacterEvent(attackerId, {
              type: 'RECOVERY_COMPLETE',
            });
          }
        }
        
        this.invokeAttackTick(activeAttack, phase, normalizedTime, deltaTime);
        this.processAttackEvents(activeAttack, attacker, normalizedTime);

        // Safety: If disableHitbox event is missed (can happen with masked layer timing edge-cases),
        // force the hitbox off once we pass the configured disable time.
        if (activeAttack.weaponInstance?.hitboxEnabled && !activeAttack.forcedHitboxOff) {
          const events = activeAttack.attackDef.events;
          const disableEvent = events?.find((e) => e.type === 'disableHitbox');
          if (disableEvent && normalizedTime >= disableEvent.time) {
            activeAttack.weaponInstance.hitboxEnabled = false;
            activeAttack.forcedHitboxOff = true;
            logger.core.warn(
              `[CombatSystem] Forced hitbox OFF for ${attackerId} (${activeAttack.attackDef.id}) at norm=${normalizedTime.toFixed(3)} (missed disableHitbox@${disableEvent.time})`
            );
          }
        }
        
        // End attack when animation reaches recovery phase or exit time
        if (attackType === 'channeled') {
          this.updateChanneledAttack(activeAttack, attacker, deltaTime);
        } else {
          // Do not end early at 'recovery' — that allows restarting an attack while the
          // one-shot animation is still visibly playing, which feels like self-interrupt spam.
          const shouldEnd = normalizedTime >= 1.0;
          
          const elapsed = this.getTimeSeconds() - (activeAttack.startTime ?? this.getTimeSeconds());
          if (elapsed > 0.3) { // Log if attack takes longer than 300ms
            logger.core.debug(`[CombatSystem] Attack still active: ${attackerId}, elapsed=${elapsed.toFixed(2)}s, norm=${normalizedTime.toFixed(2)}, phase=${phase}, shouldEnd=${shouldEnd}`);
          }
          
          if (shouldEnd) {
            logger.core.log(`[CombatSystem] Attack complete for ${attackerId} (normalizedTime=${normalizedTime.toFixed(2)}, phase=${phase})`);
            this.endAttack(attackerId, { completed: true, phase });
          } else {
            const elapsed = this.getTimeSeconds() - (activeAttack.startTime ?? this.getTimeSeconds());
            // Safety net: if normalized time is invalid or animation fails to finish, end after 3s
            if (!Number.isFinite(normalizedTime) || elapsed > 3) {
              logger.core.warn(`CombatSystem: Forcing attack end for ${attackerId} (normalizedTime=${normalizedTime}, elapsed=${elapsed.toFixed(2)}s)`);
              this.endAttack(attackerId, { completed: true, phase });
            }
          }
        }
      }
    }

    // Update projectiles via ProjectileSystem (movement, hit detection, lifetime)
    // ProjectileSystem returns hit results for damage application
    const projectileHitResults = this.projectileSystem.update(deltaTime, characters);
    
    // Process hit results - apply damage for each hit
    for (const hit of projectileHitResults) {
      const target = characters.find((c) => c.getId() === hit.targetId);
      
      // Find owner from the active attack that spawned this projectile
      const ownerAttack = Array.from(this.activeAttacks.entries()).find(
        ([_id, attack]) => attack.attackDef.id === hit.attackDefId
      );
      const ownerId = ownerAttack?.[0];
      const ownerAttacker = ownerId ? characters.find(c => c.getId() === ownerId) : undefined;
      
      if (target) {
        const activeAttack = ownerId ? this.activeAttacks.get(ownerId) : undefined;
        
        this.applyDamage(
          target,
          ownerId ?? 'unknown',
          hit.damage,
          hit.position,
          {
            attackId: hit.attackDefId,
            attackDef: activeAttack?.attackDef,
            source: 'projectile',
            impactDirection: hit.impactDirection,
            impactNormal: hit.normal,
            projectileId: hit.projectileId,
            impactFX: hit.impactFX, // Use impactFX from projectile hit result (survives attack end)
          },
          ownerAttacker
        );
      }
    }

    // Update weapons (hit detection during active windows)
    for (const [_id, weapon] of this.activeWeapons) {
      this.updateWeapon(weapon, deltaTime, characters);
    }
  }

  /**
   * Update weapon hit detection during active windows.
   * Uses weapon collision mesh intersection with target character capsules.
   */
  private updateWeapon(weapon: WeaponInstance, _deltaTime: number, characters: Character[]): void {
    if (!weapon.hitboxEnabled) return;

    // Get weapon owner (attacker)
    const activeAttack = Array.from(this.activeAttacks.values()).find(
      (attack) => attack.weaponInstance?.id === weapon.id
    );
    if (!activeAttack) return;

    const attacker = characters.find((c) => c.getId() === activeAttack.attackerId);
    if (!attacker) return;

    // Only check once per enabled window (track in weapon instance)
    if (weapon.hasHit) return;

    // Get the mesh to use for collision detection
    const collisionMesh = weapon.collisionMesh ?? weapon.weaponMesh;
    
    // Get attacker's facing direction for impact calculations
    const characterRotation = attacker.character.rotation.y;
    const direction = new Vector3(
      Math.sin(characterRotation),
      0,
      Math.cos(characterRotation)
    );
    const normalizedDirection = direction.normalizeToNew();

    let hitSomething = false;
    let closestHit: { target: Character; distance: number } | null = null;

    // Check weapon collision mesh against all potential targets
    for (const target of characters) {
      // Skip self
      if (target.getId() === activeAttack.attackerId) continue;
      
      // Skip dead characters - they can't be hit by weapons
      if (target.getIsDead()) continue;
      
      // Check if weapon mesh intersects with target's capsule
      const targetMesh = target.character;
      if (collisionMesh.intersectsMesh(targetMesh, true)) {
        // Calculate distance for closest-hit selection
        const distance = Vector3.Distance(
          collisionMesh.getAbsolutePosition(),
          targetMesh.getAbsolutePosition()
        );
        
        if (!closestHit || distance < closestHit.distance) {
          closestHit = { target, distance };
        }
      }
    }

    // Apply damage to closest hit target only
    if (closestHit) {
      const damage = activeAttack.attackDef.damage ?? 10;
      const target = closestHit.target;
      
      // Get impact position (weapon position)
      const impactPosition = collisionMesh.getAbsolutePosition();

      this.applyDamage(
        target,
        activeAttack.attackerId,
        damage,
        impactPosition,
        {
          attackId: activeAttack.attackDef.id,
          attackDef: activeAttack.attackDef,
          source: 'melee',
          impactDirection: normalizedDirection,
          impactNormal: normalizedDirection.scale(-1), // Normal faces back toward attacker
          weaponId: weapon.id,
        },
        attacker
      );

      logger.core.info(
        `CombatSystem: ⚔️ Melee hit on ${target.getId()} for ${damage} damage (mesh intersection, ${weapon.hasDedicatedCollisionMesh ? 'dedicated collision mesh' : 'weapon mesh bbox'})`
      );

      hitSomething = true;
    }

    // Also check for damageable objects (destructibles) using mesh intersection
    if (!hitSomething) {
      const objectHit = this.checkWeaponVsDamageableObjects(
        collisionMesh, 
        activeAttack.attackDef.damage ?? 10, 
        normalizedDirection
      );
      if (objectHit) {
        hitSomething = true;
      }
    }

    if (hitSomething) {
      weapon.hasHit = true; // Mark as hit to prevent multiple hits during this swing
    } else {
      logger.core.debug(`CombatSystem: Melee swing missed (checked ${characters.length - 1} targets)`);
    }
  }

  /**
   * Check weapon collision mesh against damageable objects (destructibles).
   */
  private checkWeaponVsDamageableObjects(
    weaponMesh: AbstractMesh,
    damage: number,
    _impactDirection: Vector3
  ): boolean {
    const stateManager = StateManager.getInstance();
    const damageableMeshes = stateManager.getAllDamageableMeshes();
    
    if (damageableMeshes.length === 0) {
      return false;
    }

    for (const mesh of damageableMeshes) {
      if (weaponMesh.intersectsMesh(mesh, true)) {
        // Found intersection - get object ID and apply damage
        const objectId = stateManager.getObjectIdFromMesh(mesh);
        if (objectId) {
          stateManager.applyDamageToObject(objectId, damage);
          logger.core.info(`CombatSystem: ⚔️ Melee hit destructible ${objectId} for ${damage} damage`);
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Apply damage to a character and emit combat events.
   */
  private applyDamage(
    target: Character,
    attackerId: string,
    damage: number,
    position: Vector3,
    context?: {
      attackId?: string;
      attackDef?: AttackDef;
      source?: 'melee' | 'projectile' | 'aoe';
      impactDirection?: Vector3;
      impactNormal?: Vector3;
      weaponId?: string;
      projectileId?: string;
      impactFX?: import('./effects/FXPresets').FXConfig;
    },
    attacker?: Character
  ): void {
    const impactDirection = context?.impactDirection ? context.impactDirection.clone() : undefined;
    const impactNormal = context?.impactNormal ? context.impactNormal.clone() : undefined;

    const damageContext: CharacterDamageContext = {
      baseDamage: damage,
      attackerId,
      targetId: target.getId(),
      attackId: context?.attackId,
      source: context?.source,
      impactDirection,
      impactNormal
    };

    let appliedDamage = damage;
    const damageCallback = target.getDamageCallback();
    if (damageCallback) {
      try {
        const result = damageCallback(damageContext);
        if (typeof result === 'number' && Number.isFinite(result)) {
          appliedDamage = Math.max(0, result);
        } else {
          logger.core.warn(`CombatSystem: onDamageReceived for ${target.getId()} returned non-numeric value; using base damage ${damage}.`);
        }
      } catch (error) {
        logger.core.error(`CombatSystem: onDamageReceived callback threw for ${target.getId()}`, error);
      }
    }

    const resolvedContext: CharacterDamageContext = {
      ...damageContext,
      finalDamage: appliedDamage
    };

    const lifecycleMetadata = {
      baseDamage: damage,
      appliedDamage,
      attackId: context?.attackId,
      weaponId: context?.weaponId,
      projectileId: context?.projectileId
    };

    const killed = target.takeDamage(appliedDamage, {
      damageContext: resolvedContext,
      metadata: lifecycleMetadata
    });

    // Default behavior: damage can interrupt active attacks unless policy blocks it
    if (this.activeAttacks.has(target.getId())) {
      this.cancelAttackFor(target, 'damage');
    }

    // Track last hit for debug UI (node id matches Scene Inspector ids: String(uniqueId ?? id ?? name)).
    const n: any = (target as any).character;
    if (n) {
      const nodeId = String(n.uniqueId ?? n.id ?? n.name ?? target.getId());
      const nodeName = String(n.name ?? target.getId());
      this.lastHit = { nodeId, nodeName };
    }

    // Get impactFX - prefer context (from projectile) over attackDef (may be gone after attack ends)
    const impactFX = context?.impactFX ?? context?.attackDef?.impactFX;
    
    // Emit damage event
    this.eventBus.emit('combat:damage', {
      attackerId,
      targetId: target.getId(),
      damage: appliedDamage,
      position: position.clone(),
      source: context?.source,
      impactDirection: impactDirection ? impactDirection.clone() : undefined,
      impactNormal: impactNormal ? impactNormal.clone() : undefined,
      attackDefId: context?.attackId,
      weaponId: context?.weaponId,
      projectileId: context?.projectileId,
      impactFX,
    } as CombatDamageEvent);
    
    // Spawn impact FX
    if (impactFX) {
      const fxManager = ServiceRegistry.getFXManager();
      if (fxManager) {
        fxManager.spawnFX(impactFX, position);
      }
    }

    if (attacker) {
      const hitCallback = attacker.getAttackHitCallback();
      if (hitCallback && context?.attackDef) {
        const hitContext: CharacterHitContext = {
          attackerId,
          targetId: target.getId(),
          attack: context.attackDef,
          damage: appliedDamage
        };
        try {
          hitCallback(hitContext);
        } catch (error) {
          logger.core.error(`CombatSystem: onAttackHit callback threw for ${attackerId}`, error);
        }
      }
    }

    if (killed) {
      // Emit death event
      this.eventBus.emit('combat:death', {
        characterId: target.getId(),
        killerId: attackerId,
      } as CombatDeathEvent);
    }
  }

  getLastHitInfo(): { nodeId: string; nodeName: string } | null {
    return this.lastHit;
  }

  /**
   * Raycast against all characters (excluding owner).
   * Supports sphere sweep when hitRadius > 0.
   */
  private raycastCharacters(ray: Ray, ownerId: string, characters: Character[], hitRadius: number = 0): HitResult {
    let closestHit: HitResult = { hit: false };
    let closestDistance = Infinity;

    for (const character of characters) {
      if (character.getId() === ownerId) continue;
      
      // Skip dead characters - they can't be hit by projectiles
      if (character.getIsDead()) continue;

      if (hitRadius > 0) {
        // Sphere sweep: check if the ray comes within hitRadius of character center
        // This is more forgiving than a thin raycast
        const characterPos = character.character.getAbsolutePosition();
        const characterRadius = 0.5; // Approximate character collision radius
        const combinedRadius = hitRadius + characterRadius;
        
        // Find closest point on ray segment to character center
        const rayDir = ray.direction;
        const toChar = characterPos.subtract(ray.origin);
        const t = Math.max(0, Math.min(ray.length, Vector3.Dot(toChar, rayDir)));
        const closestPoint = ray.origin.add(rayDir.scale(t));
        const distToChar = Vector3.Distance(closestPoint, characterPos);
        
        if (distToChar <= combinedRadius && t < closestDistance) {
          closestDistance = t;
          closestHit = {
            hit: true,
            targetId: character.getId(),
            position: closestPoint,
            normal: characterPos.subtract(closestPoint).normalize(),
            distance: t,
          };
        }
      } else {
        // Thin raycast (original behavior)
        const pickInfo = ray.intersectsMesh(character.character);
        if (pickInfo.hit && pickInfo.distance < closestDistance) {
          closestDistance = pickInfo.distance;
          closestHit = {
            hit: true,
            targetId: character.getId(),
            position: pickInfo.pickedPoint ?? undefined,
            normal: pickInfo.getNormal() ?? undefined,
            distance: pickInfo.distance,
          };
        }
      }
    }

    return closestHit;
  }

  /**
   * Generate unique instance ID.
   */
  private generateInstanceId(): string {
    return `combat_${this.nextInstanceId++}`;
  }

  /**
   * Get debug info for all active instances.
   */
  getDebugInfo(): {
    weapons: WeaponInstance[];
    projectiles: ProjectileInstance[];
  } {
    return {
      weapons: Array.from(this.activeWeapons.values()),
      projectiles: this.projectileSystem.getActiveProjectiles(),
    };
  }
  
  /**
   * Get serializable weapon info for debug UI (can't send raw BabylonJS objects).
   */
  getDebugWeaponInfo() {
    return Array.from(this.activeWeapons.values()).map(weapon => ({
      id: weapon.id,
      ownerId: weapon.ownerId,
      attackDefId: weapon.attackDefId,
      hitboxEnabled: weapon.hitboxEnabled,
      hasHit: weapon.hasHit,
      hasDedicatedCollisionMesh: weapon.hasDedicatedCollisionMesh,
      attachedBone: weapon.attachedBone,
      weaponMeshName: weapon.weaponMesh.name,
      collisionMeshName: weapon.collisionMesh?.name,
      animationGroup: weapon.animationGroup ? {
        name: weapon.animationGroup.name,
        isPlaying: weapon.animationGroup.isPlaying,
        speedRatio: weapon.animationGroup.speedRatio,
      } : null,
    }));
  }
  
  /**
   * Get serializable projectile info for debug UI.
   */
  getDebugProjectileInfo() {
    return this.projectileSystem.getDebugInfo();
  }
  
  /**
   * Enable/disable combat debug rendering (hitboxes, collision volumes).
   */
  setDebugEnabled(enabled: boolean): void {
    this.debugEnabled = enabled;
    
    if (!enabled) {
      // Clear all debug visualizations
      this.debugVisualizations.forEach((meshes) => {
        meshes.forEach(mesh => this.debugRenderer.remove(mesh));
      });
      this.debugVisualizations.clear();
    }
  }
  
  /**
   * Render debug visualizations for active weapons and projectiles.
   * Called each frame from main loop when debug enabled.
   * 
   * OPTIMIZED: Only recreates visualizations when instances change, 
   * updates positions for existing ones to avoid expensive text label recreation.
   */
  renderDebug(characters: Character[]): void {
    if (!this.debugEnabled) return;
    
    // Update wireframe meshes to follow their source meshes
    this.debugRenderer.updateWireframes();
    
    // Track which instances are still active
    const activeIds = new Set<string>();
    
    // Update character capsule visualizations (only create if new)
    for (const character of characters) {
      const vizId = `character_${character.getId()}`;
      activeIds.add(vizId);
      
      const capsuleMesh = character.character;
      const existingViz = this.debugVisualizations.get(vizId);
      
      if (existingViz) {
        // Update existing capsule position (capsule is at index 0, label at index 1)
        existingViz[0].position.copyFrom(capsuleMesh.position);
        // Label follows via billboard
      } else {
        // Create new visualization (only on first appearance)
        const meshes: AbstractMesh[] = [];
        
        const capsuleViz = this.debugRenderer.createCapsule(
          capsuleMesh.position.clone(),
          1.8,
          0.4,
          new Color3(0, 1, 0),
          true
        );
        meshes.push(capsuleViz);
        
        // Label (expensive, only created once)
        const labelPos = capsuleMesh.position.clone().add(new Vector3(0, 1.2, 0));
        const label = this.debugRenderer.createTextLabel(
          character.getId(),
          labelPos,
          new Color3(0, 1, 0)
        );
        label.parent = capsuleViz; // Parent to capsule so it follows
        label.position = new Vector3(0, 1.2, 0);
        meshes.push(label);
        
        this.debugVisualizations.set(vizId, meshes);
      }
    }
    
    // Update weapon visualizations (recreate only when state changes)
    for (const [id, weapon] of this.activeWeapons) {
      activeIds.add(id);
      
      const existingViz = this.debugVisualizations.get(id);
      const color = weapon.hitboxEnabled ? new Color3(1, 1, 0) : new Color3(0.5, 0.5, 0.5);
      
      // Only recreate if doesn't exist or hitbox state changed
      if (!existingViz) {
        const meshes: AbstractMesh[] = [];
        
        let hitboxViz: AbstractMesh;
        if (weapon.hasDedicatedCollisionMesh && weapon.collisionMesh) {
          // Show actual collision geometry as wireframe
          hitboxViz = this.debugRenderer.createMeshWireframe(weapon.collisionMesh, color);
        } else {
          // No dedicated collision mesh - show bounding box
          hitboxViz = this.debugRenderer.createBoundingBoxWireframe(weapon.weaponMesh, color);
        }
        meshes.push(hitboxViz);
        
        // Label (expensive, created once)
        const weaponPos = weapon.weaponMesh.getAbsolutePosition();
        const labelText = weapon.hasDedicatedCollisionMesh ? 
          `${id} (dedicated)` : 
          `${id} (bbox)`; 
        const label = this.debugRenderer.createTextLabel(
          labelText,
          weaponPos.add(new Vector3(0, 0.3, 0)),
          color
        );
        label.parent = hitboxViz; // Follow hitbox
        label.position = new Vector3(0, 0.3, 0);
        meshes.push(label);
        
        this.debugVisualizations.set(id, meshes);
      } else {
        // Update color based on hitbox state (cheaper than recreating)
        const hitboxViz = existingViz[0];
        const material = hitboxViz.material as any;
        if (material && material.emissiveColor) {
          material.emissiveColor.copyFrom(color);
        }
      }
    }
    
    // Update projectile visualizations (update positions only)
    for (const projectile of this.projectileSystem.getActiveProjectiles()) {
      const id = projectile.id;
      activeIds.add(id);
      
      const existingViz = this.debugVisualizations.get(id);
      
      if (existingViz) {
        // Update positions (cheap)
        // Index 0: bounding box, 1: velocity arrow (if exists), last: label
        // Projectile bbox follows mesh automatically via parent
        // Just update arrow if it exists
        if (existingViz.length > 2 && projectile.velocity.length() > 0.1) {
          // Arrow exists, update it
          existingViz[1].position.copyFrom(projectile.position);
        }
      } else {
        // Create new visualization (only on spawn)
        const meshes: AbstractMesh[] = [];
        
        const projViz = this.debugRenderer.createBoundingBoxWireframe(
          projectile.mesh,
          new Color3(1, 0, 0)
        );
        meshes.push(projViz);
        
        // Velocity arrow (optional)
        if (projectile.velocity.length() > 0.1) {
          const velocityArrow = this.debugRenderer.createArrow(
            projectile.position.clone(),
            projectile.velocity.normalize(),
            Math.min(projectile.velocity.length() * 0.1, 1.0),
            new Color3(1, 0.5, 0)
          );
          meshes.push(velocityArrow);
        }
        
        // Label (expensive, created once)
        const labelText = `${id.substring(0, 15)}`; // Shorter ID
        const label = this.debugRenderer.createTextLabel(
          labelText,
          projectile.position.add(new Vector3(0, 0.3, 0)),
          new Color3(1, 0, 0)
        );
        label.parent = projViz; // Follow projectile
        label.position = new Vector3(0, 0.3, 0);
        meshes.push(label);
        
        this.debugVisualizations.set(id, meshes);
      }
    }
    
    // Clean up visualizations for instances that no longer exist
    const toRemove: string[] = [];
    this.debugVisualizations.forEach((meshes, id) => {
      if (!activeIds.has(id)) {
        meshes.forEach(mesh => this.debugRenderer.remove(mesh));
        toRemove.push(id);
      }
    });
    toRemove.forEach(id => this.debugVisualizations.delete(id));
  }
}
